import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scrollToTop } from '../utils/scrollUtils';
import {
  Container,
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Tab,
  Tabs,
  Card,
  CardContent,
  LinearProgress,
  Chip
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SchoolIcon from '@mui/icons-material/School';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import EmergencyShareIcon from '@mui/icons-material/EmergencyShare';
import { useModuleProgress } from '../context/ModuleProgressContext';
import CourseModule from '../components/CourseModule';
import { getCompletedLessons } from '../services/lessonService';

// Course module data
export const module1_lessons = [
  {
    id: '1.1',
    title: 'Course Introduction',
    description: 'Overview of the BLS and CPR training course structure and objectives',
    duration: 15,
    type: 'Introduction',
    content: {
      introduction: 'Welcome to the Basic Life Support (BLS) and CPR Training course. This comprehensive program will equip you with the essential skills and knowledge needed to respond effectively in emergency situations.',
      objectives: [
        'Understand the importance of quick response in emergency situations',
        'Learn proper CPR techniques and protocols',
        'Master the use of AED devices',
        'Develop confidence in emergency response'
      ],
      mainContent: {
        whatIsBLS: {
          title: 'What is Basic Life Support?',
          content: 'Basic Life Support (BLS) is a level of medical care used for victims of life-threatening illnesses or injuries until they can be given full medical care at a hospital. It includes cardiopulmonary resuscitation (CPR), control of bleeding, basic airway management, spinal immobilization, and other techniques.'
        },
        courseOverview: {
          title: 'Course Overview',
          content: 'This course consists of 8 comprehensive modules covering everything from basic concepts to advanced techniques. Each module includes a mix of theoretical knowledge and practical skills.',
          modulesList: [
            'Introduction and Course Overview',
            'Patient Assessment and Scene Safety',
            'Adult Basic Life Support',
            'Practical BLS Techniques',
            'AED Usage and Safety',
            'Post-Resuscitation Care',
            'Emergency Situations',
            'Course Summary and Assessment'
          ]
        },
        learningMethods: {
          title: 'Learning Methods',
          content: 'The course utilizes various teaching methods including:',
          methods: [
            'Interactive video demonstrations',
            'Hands-on practice sessions',
            'Case studies and scenarios',
            'Knowledge checks and assessments',
            'Practical skills evaluation'
          ]
        },
        certification: {
          title: 'Certification',
          content: 'Upon successful completion of this course, you will receive a BLS certification valid for 2 years. This certification is recognized by healthcare institutions and meets international standards for basic life support training.'
        }
      },
      keyPoints: [
        'BLS is a critical first response in emergency situations',
        'Quick action and proper technique can save lives',
        'Certification requires both knowledge and practical skill demonstration',
        'Regular practice and updates are essential for maintaining skills'
      ],
      summary: 'This course will prepare you to handle emergency situations with confidence and competence. By the end, you will have the knowledge and skills to potentially save lives through proper BLS techniques.'
    }
  },
  {
    id: '1.2',
    title: 'Course Navigation Guide',
    description: 'Learn how to navigate through the course modules and track your progress',
    duration: 10,
    type: 'Tutorial',
    content: {
      introduction: 'This section will help you understand how to navigate through the course effectively and make the most of your learning experience.',
      objectives: [
        'Understand the course platform navigation',
        'Learn to track your progress',
        'Access course resources and support',
        'Use interactive features effectively'
      ],
      mainContent: {
        navigation: {
          title: 'Navigating the Course',
          content: 'The course is organized into clear sections with intuitive navigation. Each module contains multiple lessons that you can access sequentially or revisit as needed.',
          features: [
            'Progress tracking',
            'Bookmarking capability',
            'Resource downloads',
            'Interactive elements'
          ]
        },
        progressTracking: {
          title: 'Tracking Your Progress',
          content: 'Your progress is automatically saved as you complete each section. Green checkmarks indicate completed sections, while unchecked circles show pending lessons.'
        },
        support: {
          title: 'Getting Support',
          content: 'If you need assistance, you can access help through:',
          supportChannels: [
            'Technical support chat',
            'Course instructor contact',
            'FAQ section',
            'Resource library'
          ]
        }
      },
      keyPoints: [
        'Complete each section sequentially for best results',
        'Practice exercises are essential for skill development',
        'Review materials are available for each module',
        'Certificate is awarded upon full course completion'
      ]
    }
  },
  {
    id: '1.3',
    title: 'Learning Objectives and Outcomes',
    description: 'Understanding the course goals and expected outcomes',
    duration: 20,
    type: 'Lesson',
    content: {
      introduction: 'This section outlines what you will learn throughout the course and the skills you will develop.',
      objectives: [
        'Understand course learning objectives',
        'Review skill requirements',
        'Learn assessment criteria',
        'Understand certification requirements'
      ],
      mainContent: {
        learningObjectives: {
          title: 'Course Learning Objectives',
          objectives: [
            {
              category: 'Knowledge',
              items: [
                'Understanding of basic life support principles',
                'Recognition of life-threatening situations',
                'Knowledge of emergency response protocols',
                'Understanding of AED operation'
              ]
            },
            {
              category: 'Skills',
              items: [
                'Performing high-quality chest compressions',
                'Delivering effective rescue breaths',
                'Using AED safely and effectively',
                'Conducting primary patient assessment'
              ]
            },
            {
              category: 'Attitudes',
              items: [
                'Confidence in emergency response',
                'Commitment to maintaining skills',
                'Professional approach to patient care',
                'Understanding of ethical considerations'
              ]
            }
          ]
        },
        certificationRequirements: {
          title: 'Certification Requirements',
          requirements: [
            'Complete all course modules',
            'Pass written assessment with 80% or higher',
            'Successfully demonstrate practical skills',
            'Complete hands-on evaluation'
          ]
        }
      },
      summary: 'By meeting these objectives, you will be prepared to provide effective basic life support in emergency situations.'
    }
  }
];

export const module2_lessons = [
  {
    id: '2.1',
    title: 'Introduction to Basic Life Support',
    description: 'Overview of BLS fundamentals and core concepts',
    duration: 25,
    type: 'Introduction',
    content: {
      introduction: 'Welcome to the Basic Life Support module. This comprehensive introduction will establish the foundation for understanding and performing basic life support effectively.',
      objectives: [
        'Understand the core principles of Basic Life Support',
        'Learn the key components of the BLS sequence',
        'Recognize the importance of early intervention',
        'Master the initial assessment approach'
      ],
      mainContent: {
        fundamentals: {
          title: 'Core Principles of BLS',
          content: 'Basic Life Support is built on four fundamental principles:',
          principles: [
            {
              name: 'Early Recognition',
              description: 'Quickly identifying life-threatening emergencies and potential cardiac arrest situations.'
            },
            {
              name: 'Early CPR',
              description: 'Initiating high-quality chest compressions and rescue breaths as soon as cardiac arrest is identified.'
            },
            {
              name: 'Early Defibrillation',
              description: 'Rapid access to and use of an AED when indicated.'
            },
            {
              name: 'Early Advanced Care',
              description: 'Timely transition to advanced life support and post-resuscitation care.'
            }
          ]
        },

        blsSequence: {
          title: 'The BLS Sequence',
          steps: [
            {
              step: 1,
              title: 'Scene Safety',
              description: 'Ensure the environment is safe for both rescuer and victim. Check for hazards like electrical wires, traffic, fire, or toxic substances.',
              icon: 'Security',
              color: '#f44336'  // Red for safety
            },
            {
              step: 2,
              title: 'Check Response',
              description: 'Determine consciousness by tapping shoulders and asking loudly "Are you okay?" Look for any response including movement, speech, or groaning.',
              icon: 'TouchApp',
              color: '#2196f3'  // Blue for checking
            },
            {
              step: 3,
              title: 'Call for Help',
              description: 'If unresponsive, activate emergency response system (911). If alone with an adult, call first. For children, perform 2 minutes of CPR first.',
              icon: 'Call',
              color: '#ff9800'  // Orange for urgency
            },
            {
              step: 4,
              title: 'Check Breathing and Pulse',
              description: 'Look for chest rise, listen for breath sounds, feel for air flow. Check carotid pulse for adults/children, brachial for infants. Take no more than 10 seconds.',
              icon: 'Favorite',
              color: '#4caf50'  // Green for vitals
            },
            {
              step: 5,
              title: 'Begin CPR if Needed',
              description: 'If no normal breathing or pulse, begin high-quality CPR. Push hard and fast in center of chest.',
              icon: 'FitnessCenter',
              color: '#e91e63'  // Pink for action
            },
            {
              step: 6,
              title: 'Use AED',
              description: 'As soon as AED arrives, turn it on and follow prompts. Minimize interruptions in chest compressions.',
              icon: 'Power',
              color: '#9c27b0'  // Purple for equipment
            },
            {
              step: 7,
              title: 'Continue Care',
              description: 'Continue CPR and AED use until victim shows signs of life or advanced medical help arrives.',
              icon: 'Loop',
              color: '#607d8b'  // Blue grey for continuation
            }
          ]
        },
        importanceOfTime: {
          title: 'The Critical Time Factor',
          content: 'In cardiac arrest, every minute without CPR decreases survival chance by 7-10%. Brain damage can begin within 4-6 minutes without oxygen.',
          principles: [
            {
              name: 'Chain of Survival',
              description: 'Early recognition and activation, Early CPR, Early defibrillation, Advanced care, Post-cardiac arrest care'
            },
            {
              name: 'Response Windows',
              description: 'Brain death: 4-6 minutes without oxygen, Biological death: 8-10 minutes, Permanent death: >10 minutes without intervention'
            },
            {
              name: 'Quality CPR Impact',
              description: 'High-quality immediate CPR can double or triple survival rates from cardiac arrest'
            },
            {
              name: 'Defibrillation Timing',
              description: 'For every minute delay in defibrillation, survival rates decrease by 7-10% if no CPR is provided'
            }
          ]
        },
        // initialAssessment: {
        //   title: 'Initial Assessment Approach',
        //   content: 'The initial assessment follows the DR ABC protocol:',
        //   protocol: [
        //     {
        //       letter: 'D',
        //       meaning: 'Danger',
        //       action: 'Check for hazards to yourself and others'
        //     },
        //     {
        //       letter: 'R',
        //       meaning: 'Response',
        //       action: 'Check if the victim is responsive'
        //     },
        //     {
        //       letter: 'A',
        //       meaning: 'Airway',
        //       action: 'Ensure the airway is open and clear'
        //     },
        //     {
        //       letter: 'B',
        //       meaning: 'Breathing',
        //       action: 'Look, listen, and feel for normal breathing'
        //     },
        //     {
        //       letter: 'C',
        //       meaning: 'Circulation',
        //       action: 'Check for signs of circulation'
        //     }
        //   ]
        // }
      },
      keyPoints: [
        'Time is critical in emergency situations',
        'Following the proper sequence saves lives',
        'Safety of the rescuer is paramount',
        'Early recognition and response are crucial'
      ],
      quiz: {
        title: 'Knowledge Check',
        description: 'Test your understanding of Basic Life Support fundamentals',
        questions: [
          {
            id: 1,
            question: 'What is the first step in the BLS sequence?',
            options: [
              'Start chest compressions',
              'Check for scene safety',
              'Call emergency services',
              'Give rescue breaths'
            ],
            correctAnswer: 1,
            explanation: 'Scene safety is always the first priority to ensure the safety of both rescuer and victim.'
          },
          {
            id: 2,
            question: 'What does the acronym CAB stand for in BLS?',
            options: [
              'Circulation, Airway, Breathing',
              'Compressions, Airway, Breathing',
              'Cardiac, Arterial, Blood',
              'Check, Alert, Breathe'
            ],
            correctAnswer: 1,
            explanation: 'CAB stands for Circulation, Airway, Breathing - the order of priority in modern BLS protocols.'
          },
          {
            id: 3,
            question: 'How soon should CPR be started after cardiac arrest for best outcomes?',
            options: [
              'Within 10 minutes',
              'Within 6 minutes',
              'Within 4 minutes',
              'Immediately'
            ],
            correctAnswer: 3,
            explanation: 'CPR should be started immediately as brain damage can begin within 4-6 minutes of cardiac arrest.'
          },
          {
            id: 4,
            question: 'Which of the following is a sign of effective chest compressions?',
            options: [
              'Visible chest rise and fall',
              'Cracking sounds from the chest',
              'Patient movement',
              'Rescuer fatigue'
            ],
            correctAnswer: 0,
            explanation: 'Visible chest rise and fall indicates proper depth and recoil during compressions.'
          },
          {
            id: 5,
            question: 'What is the recommended depth for chest compressions in adult CPR?',
            options: [
              '1-2 inches',
              '2-2.4 inches',
              '3-4 inches',
              '4-5 inches'
            ],
            correctAnswer: 1,
            explanation: 'The recommended depth for adult chest compressions is 2-2.4 inches (5-6 cm).'
          }
        ]
      },
      practicalTips: [
        'Always ensure scene safety before approaching',
        'Call for help before starting CPR if alone',
        'Use proper personal protective equipment when available',
        'Stay calm and focused during assessment'
      ],
      commonMistakes: [
        'Rushing in without checking scene safety',
        'Delayed activation of emergency response',
        'Incomplete or hurried initial assessment',
        'Forgetting personal protection'
      ],
      summary: 'Understanding these fundamental concepts of BLS is crucial for providing effective emergency care. The systematic approach taught in this module will help you respond confidently and effectively in emergency situations.'
    }
  },
  {
    id: '2.2',
    title: 'Purpose and Importance of BLS',
    description: 'Understanding why BLS is critical in emergency care',
    duration: 20,
    type: 'Lesson',
    content: {
      introduction: 'The primary purpose of Basic Life Support (BLS) is to maintain the circulation of oxygenated blood to the brain and other vital organs during cardiac arrest or respiratory failure. This initial intervention is critical in sustaining life until more advanced medical care can be provided.',
      objectives: [
        'Understand the fundamental purpose of BLS',
        'Learn the key aspects of BLS importance',
        'Recognize the impact of BLS on survival rates',
        'Understand the role of BLS in the healthcare system'
      ],
      mainContent: {
        primaryPurpose: {
          title: 'Primary Purpose of BLS',
          content: 'BLS encompasses a set of life-saving techniques, including:',
          keyComponents: [
            {
              component: 'Immediate Response',
              description: 'BLS enables first responders, including laypersons and professionals, to act quickly and effectively in the event of a medical emergency.'
            },
            {
              component: 'Cardiopulmonary Resuscitation',
              description: 'Effective delivery of chest compressions and rescue breaths to maintain vital organ function.'
            },
            {
              component: 'AED Usage',
              description: 'Proper use of automated external defibrillators to restore normal heart rhythm when indicated.'
            },
            {
              component: 'Airway Management',
              description: 'Basic techniques to ensure and maintain a clear airway for breathing.'
            }
          ]
        },
        importanceFactors: {
          title: 'Importance of BLS',
          factors: [
            {
              factor: 'Immediate Response',
              description: 'Basic Life Support equips people to take quick action during emergencies, especially in those first few minutes when every second counts and before medical professionals arrive.',
              importance: 'Critical'
            },
            {
              factor: 'Survival Rates',
              description: 'Performing BLS correctly and without delay—such as starting chest compressions and giving rescue breaths—can significantly raise the chances of surviving cardiac arrest by keeping blood and oxygen flowing.',
              importance: 'High'
            },
            {
              factor: 'Workplace Safety',
              description: 'In jobs with higher safety risks like construction or manufacturing, BLS training helps employees handle emergencies effectively, reducing the impact of accidents or injuries.',
              importance: 'Essential'
            },
            {
              factor: 'Community Preparedness',
              description: 'Training more people in BLS creates safer communities by ensuring that help is available quickly in public places, schools, or homes when emergencies happen.',
              importance: 'Significant'
            },
            {
              factor: 'Foundation for Advanced Care',
              description: 'BLS serves as the first step in emergency treatment. It prepares patients for more advanced medical care by helping to stabilize their condition early on.',
              importance: 'Fundamental'
            }
          ]
        },
        
        keyBenefits: {
          title: 'Key Benefits of BLS',
          benefits: [
            {
              benefit: 'Recognition of Life-Threatening Conditions',
              description: 'Early identification of emergency situations'
            },
            {
              benefit: 'Building a Chain of Survival',
              description: 'Creating a systematic approach to emergency response'
            },
            {
              benefit: 'Improving Survival Rates',
              description: 'Enhancing outcomes through immediate intervention'
            },
            {
              benefit: 'Empowering Healthcare Professionals',
              description: 'Providing essential skills for medical personnel'
            }
          ]
        }
      },
      statistics: {
        title: 'Impact Statistics',
        data: [
          'Survival rates can increase by 200-300% with immediate BLS',
          'For every minute without CPR, survival chances decrease by 7-10%',
          'Over 350,000 cardiac arrests occur outside hospitals annually',
          'BLS-trained bystanders can triple survival rates'
        ]
      },
      keyPoints: [
        'BLS is the foundation of emergency cardiac care',
        'Early intervention significantly improves outcomes',
        'Community-wide training enhances public safety',
        'BLS skills are essential for healthcare providers'
      ],
      summary: 'The importance of BLS cannot be overstated. It serves as the critical first link in the chain of survival, providing essential life-saving interventions until advanced medical care becomes available. Understanding and mastering BLS is crucial for healthcare providers and community members alike.',
      quiz: {
        title: 'Knowledge Check',
        description: 'Test your understanding of the purpose and importance of BLS',
        questions: [
          {
            id: '2.2.1',
            question: 'What is the primary purpose of Basic Life Support (BLS)?',
            options: [
              'To provide advanced medical procedures',
              'To maintain circulation of oxygenated blood to vital organs',
              'To administer medications in emergencies',
              'To diagnose medical conditions'
            ],
            correctAnswer: 1,
            explanation: 'The primary purpose of BLS is to maintain the circulation of oxygenated blood to the brain and other vital organs during cardiac arrest or respiratory failure.'
          },
          {
            id: '2.2.2',
            question: 'According to the impact statistics, how much can survival rates increase with immediate BLS?',
            options: [
              '50-100%',
              '100-150%',
              '200-300%',
              '400-500%'
            ],
            correctAnswer: 2,
            explanation: 'According to the impact statistics, survival rates can increase by 200-300% with immediate BLS intervention.'
          },
          {
            id: '2.2.3',
            question: 'Which of the following best describes how BLS contributes to the chain of survival?',
            options: [
              'It replaces the need for advanced medical care',
              'It serves as the final step in emergency response',
              'It forms the foundation for all further life-saving measures',
              'It is only relevant for healthcare professionals'
            ],
            correctAnswer: 2,
            explanation: 'BLS forms the foundation for all further life-saving measures, providing crucial initial support until advanced medical care becomes available.'
          }
        ]
      }
    }
  },
  {
    id: '2.3',
    title: 'Legal Considerations Related to Basic Life Support',
    description: 'Understanding legal and ethical aspects of BLS in the workplace',
    duration: 25,
    type: 'Lesson',
    content: {
      introduction: 'Legal considerations in Basic Life Support are crucial for ensuring workplace safety and compliance while protecting both caregivers and patients. Understanding these legal aspects helps create a safe and compliant environment for providing emergency care.',
      objectives: [
        'Understand the duty of care and legal obligations',
        'Learn about regulatory compliance requirements',
        'Comprehend first aid regulations and their implications',
        'Recognize the importance of proper documentation'
      ],
      mainContent: {
        legalFramework: {
          title: 'Legal Framework and Obligations',
          content: 'Understanding the legal framework surrounding BLS is essential for workplace safety and compliance.',
          keyComponents: [
            {
              component: 'Duty of Care',
              description: 'Employers have a legal obligation under the Health and Safety at Work Act 1974 to ensure the health, safety, and welfare of their employees.'
            },
            {
              component: 'Regulatory Compliance',
              description: 'Employers must ensure that their BLS training programs adhere to these standards to mitigate legal risks and liabilities.'
            },
            {
              component: 'First Aid Regulations',
              description: 'According to the Health and Safety (First-Aid) Regulations 1981, workplaces are required to provide adequate and appropriate equipment, facilities, and personnel to ensure employees receive immediate attention if they are injured or taken ill at work.'
            }
          ]
        },
        liabilityAndDocumentation: {
          title: 'Liability and Documentation Requirements',
          content: 'Proper documentation and understanding of liability are crucial aspects of BLS implementation.',
          keyPoints: [
            {
              point: 'Negligence and Liability',
              description: 'Failure to provide adequate BLS training and response mechanisms can result in legal claims of negligence if an employee or visitor suffers harm due to inadequate emergency response.'
            },
            {
              point: 'Record Keeping',
              description: 'Proper documentation of BLS training sessions, incidents, and outcomes is essential for legal compliance and for demonstrating that appropriate measures were taken to ensure workplace safety.'
            }
          ]
        },
        // ethicalConsiderations: {
        //   title: 'Ethical Considerations',
        //   principles: [
        //     {
        //       name: 'Duty of Care',
        //       description: 'The ethical obligation to provide appropriate care'
        //     },
        //     {
        //       name: 'Good Samaritan Laws',
        //       description: 'Legal protections for those providing emergency assistance'
        //     },
        //     {
        //       name: 'Accurate Documentation',
        //       description: 'Ethical responsibility to maintain accurate records'
        //     },
        //     {
        //       name: 'Confidentiality',
        //       description: 'Protecting patient privacy and information'
        //     },
        //     {
        //       name: 'Beneficence',
        //       description: 'Acting in the best interest of the patient'
        //     },
        //     {
        //       name: 'Non-maleficence',
        //       description: 'Avoiding harm in the provision of care'
        //     }
        //   ]
        // }
      },
      keyPoints: [
        'Understanding legal obligations is crucial for workplace safety',
        'Proper documentation protects both providers and recipients of care',
        'Ethical considerations must guide BLS implementation',
        'Compliance with regulations prevents legal issues'
      ],
      summary: 'Addressing these legal and ethical considerations ensures that workplaces not only comply with regulations but also foster a safe, respectful, and caring environment for all employees and visitors.',
      quiz: {
        title: 'Legal Considerations Quiz',
        description: 'Test your understanding of legal and ethical aspects of BLS',
        questions: [
          {
            id: '2.3.1',
            question: 'Under which Act do employers have a legal obligation to ensure the health, safety, and welfare of their employees?',
            options: [
              'Workplace Safety Act 1980',
              'Health and Safety at Work Act 1974',
              'Employee Protection Act 1985',
              'Occupational Safety Act 1990'
            ],
            correctAnswer: 1,
            explanation: 'The Health and Safety at Work Act 1974 establishes employers\'s legal obligation to ensure the health, safety, and welfare of their employees.'
          },
          {
            id: '2.3.2',
            question: 'What can result from failure to provide adequate BLS training and response mechanisms?',
            options: [
              'Increased profits',
              'Better workplace morale',
              'Legal claims of negligence',
              'Improved efficiency'
            ],
            correctAnswer: 2,
            explanation: 'Failure to provide adequate BLS training and response mechanisms can result in legal claims of negligence if harm occurs due to inadequate emergency response.'
          },
          {
            id: '2.3.3',
            question: 'Why is proper documentation of BLS training and incidents important?',
            options: [
              'To increase company profits',
              'To reduce training time',
              'To meet social media requirements',
              'For legal compliance and demonstrating appropriate measures'
            ],
            correctAnswer: 3,
            explanation: 'Proper documentation is essential for legal compliance and for demonstrating that appropriate measures were taken to ensure workplace safety.'
          }
        ]
      }
    }
  },
  {
    id: '2.4',
    title: 'Causes of Cardiac Arrest',
    description: 'Understanding cardiac and non-cardiac causes of cardiac arrest',
    duration: 30,
    type: 'Lesson',
    content: {
      introduction: 'Cardiac arrest occurs when the heart suddenly stops beating effectively, leading to a cessation of blood flow to the brain and other vital organs. This life-threatening condition has various causes, which can be broadly categorised into cardiac and non-cardiac origins.',
      objectives: [
        'Understand the definition of cardiac arrest',
        'Identify cardiac causes of arrest',
        'Recognize non-cardiac causes of arrest',
        'Learn to differentiate between cardiac arrest and heart attack'
      ],
      mainContent: {
        cardiacCauses: {
          title: 'Cardiac Causes',
          content: 'Primary cardiac causes are directly related to heart conditions:',
          image: '/images/causesofcaardiacarrest/1.png',
          imageAlt: 'Diagram showing cardiac causes of arrest',
          causes: [
            {
              name: 'Coronary Artery Disease (CAD)',
              description: 'The most common cause of cardiac arrest. CAD involves the narrowing or blockage of coronary arteries, leading to reduced blood flow to the heart muscle and potentially resulting in a heart attack, which can trigger cardiac arrest.'
            },
            {
              name: 'Heart Attack (Myocardial Infarction)',
              description: 'A heart attack can cause cardiac arrest when the heart muscle is severely damaged, disrupting its electrical system and causing abnormal rhythms (arrhythmias) such as ventricular fibrillation or ventricular tachycardia.'
            },
            {
              name: 'Cardiomyopathy',
              description: 'This disease affects the heart muscle, making it enlarged, thick, or rigid, which can interfere with the heart\'s ability to pump blood effectively and lead to cardiac arrest.'
            },
            {
              name: 'Heart Failure',
              description: 'Chronic heart failure weakens the heart\'s ability to pump blood efficiently, increasing the risk of sudden cardiac arrest.'
            },
            {
              name: 'Congenital Heart Disease',
              description: 'Structural abnormalities in the heart present from birth can lead to cardiac arrest, especially in younger individuals.'
            }
          ]
        },
        nonCardiacCauses: {
          title: 'Non-Cardiac Causes',
          content: 'External or systemic conditions that can lead to cardiac arrest:',
          image: '/images/causesofcaardiacarrest/2.png',
          imageAlt: 'Diagram showing non-cardiac causes of arrest',
          causes: [
            {
              name: 'Severe Blood Loss',
              description: 'Significant blood loss from trauma or internal bleeding can lead to shock and cardiac arrest due to inadequate blood flow to the heart.'
            },
            {
              name: 'Electrolyte Imbalances',
              description: 'Abnormal levels of electrolytes such as potassium, magnesium, calcium, and sodium can affect the heart\'s electrical activity, leading to arrhythmias and cardiac arrest.'
            },
            {
              name: 'Respiratory Arrest',
              description: 'Conditions that lead to a cessation of breathing, such as severe asthma, choking, or drug overdose, can result in cardiac arrest due to a lack of oxygen.'
            },
            {
              name: 'Drowning',
              description: 'Submersion in water can cause respiratory failure, leading to hypoxia (lack of oxygen) and cardiac arrest.'
            },
            {
              name: 'Electrocution',
              description: 'Electrical injuries can directly disrupt the heart\'s electrical system, causing immediate cardiac arrest.'
            }
          ]
        },
        keyDifferences: {
          title: 'Understanding Key Differences',
          comparisons: [
            {
              condition: 'Cardiac Arrest',
              description: 'Happens when your heart stops from an electrical problem'
            },
            {
              condition: 'Heart Attack',
              description: 'Happens when you have a lack of blood flow to the heart'
            }
          ]
        }
      },
      keyPoints: [
        'Cardiac arrest can have both cardiac and non-cardiac origins',
        'Early recognition of risk factors is crucial',
        'Different causes require different preventive measures',
        'Understanding the cause helps in appropriate response'
      ],
      summary: 'Understanding the various causes of cardiac arrest is crucial for prevention and appropriate response. While cardiac causes are often related to underlying heart conditions, non-cardiac causes can affect anyone and require equal attention in prevention and treatment.',
      quiz: {
        title: 'Causes of Cardiac Arrest Quiz',
        description: 'Test your understanding of cardiac arrest causes',
        questions: [
          {
            id: '2.4.1',
            question: 'What is the most common cause of cardiac arrest?',
            options: [
              'Drowning',
              'Coronary Artery Disease (CAD)',
              'Electrocution',
              'Respiratory Arrest'
            ],
            correctAnswer: 1,
            explanation: 'Coronary Artery Disease (CAD) is the most common cause of cardiac arrest, involving narrowing or blockage of coronary arteries.'
          },
          {
            id: '2.4.2',
            question: 'How does a heart attack differ from cardiac arrest?',
            options: [
              'They are the same thing',
              'Heart attack is less serious',
              'Cardiac arrest is an electrical problem, heart attack is a blood flow problem',
              'Only heart attacks can be treated'
            ],
            correctAnswer: 2,
            explanation: 'A cardiac arrest occurs when the heart stops from an electrical problem, while a heart attack happens when blood flow to the heart is blocked.'
          },
          {
            id: '2.4.3',
            question: 'Which of the following is a non-cardiac cause of cardiac arrest?',
            options: [
              'Cardiomyopathy',
              'Coronary artery disease',
              'Heart failure',
              'Severe blood loss'
            ],
            correctAnswer: 3,
            explanation: 'Severe blood loss is a non-cardiac cause of cardiac arrest, while the other options are cardiac causes related to heart conditions.'
          }
        ]
      }
    }
  },
  {
    id: '2.5',
    title: 'Chain of Survival',
    description: 'Understanding the critical steps in emergency cardiac care',
    duration: 25,
    type: 'Lesson',
    content: {
      introduction: 'The Chain of Survival is a crucial concept in emergency cardiac care, outlining a series of actions that, when performed promptly and effectively, can improve the chances of survival and recovery for individuals experiencing cardiac arrest.',
      objectives: [
        'Understand each link in the Chain of Survival',
        'Learn the importance of early recognition and response',
        'Comprehend the role of early CPR and defibrillation',
        'Understand post-resuscitation care requirements'
      ],
      mainContent: {
        chainLinks: {
          title: 'Links in the Chain of Survival',
          content: 'The Chain of Survival consists of four critical steps that must be performed quickly and effectively:',
          steps: [
            {
              name: 'Early Recognition of the Emergency and Call for Help',
              description: 'The first step involves quickly recognizing the signs of cardiac arrest, which include sudden collapse, unresponsiveness, and absence of normal breathing or only gasping.',
              details: 'Immediately call for help by dialing emergency services (999 in the UK). Clearly communicate the situation, location, and that the individual is in cardiac arrest.',
              color: '#ff7043'
            },
            {
              name: 'Early Cardiopulmonary Resuscitation (CPR)',
              description: 'Prompt initiation of CPR is vital to maintain blood flow to the brain and heart. High-quality chest compressions are the cornerstone of effective CPR.',
              color: '#4caf50'
            },
            {
              name: 'Early Defibrillation',
              description: 'Many cases of cardiac arrest are caused by ventricular fibrillation or pulseless ventricular tachycardia, which require defibrillation to restore a normal heart rhythm.',
              details: 'Use an automated external defibrillator (AED) as soon as it is available.',
              color: '#e91e63'
            },
            {
              name: 'Post-Resuscitation Care',
              description: 'The final links in the chain involve the arrival of advanced life support providers, such as paramedics or healthcare professionals, who can administer advanced interventions, medications, and therapies. Post-resuscitation care, including assessment and treatment of underlying causes, is also vital to improving long-term survival and minimising complications.',
              color: '#2196f3'
            }
          ]
        },
        importance: {
          title: 'Importance of the Chain',
          content: 'Understanding and effectively implementing each step of the Chain of Survival can significantly improve the chances of survival and favourable outcomes for individuals experiencing cardiac arrest.'
        }
      },
      keyPoints: [
        'Every minute counts in cardiac arrest response',
        'All links in the chain are equally important',
        'Early recognition and activation of emergency response is crucial',
        'High-quality CPR should be started immediately',
        'Early defibrillation significantly improves survival chances'
      ],
      summary: 'The Chain of Survival represents a series of coordinated actions that, when implemented quickly and effectively, provide the best chance of survival for cardiac arrest victims. Each link in the chain is crucial, and the strength of the chain depends on the proper execution of each step.',
      quiz: {
        title: 'Chain of Survival Quiz',
        description: 'Test your understanding of the Chain of Survival concept',
        questions: [
          {
            id: '2.5.1',
            question: 'What is the first step in the Chain of Survival?',
            options: [
              'Start CPR immediately',
              'Early recognition and call for help',
              'Use an AED',
              'Provide post-resuscitation care'
            ],
            correctAnswer: 1,
            explanation: 'The first step in the Chain of Survival is early recognition of the emergency and calling for help. This ensures that professional help is on the way while other interventions are being performed.'
          },
          {
            id: '2.5.2',
            question: 'Why is early CPR considered a crucial link in the Chain of Survival?',
            options: [
              'It completely restores heart function',
              'It wakes the patient up immediately',
              'It maintains blood flow to vital organs',
              'It eliminates the need for further care'
            ],
            correctAnswer: 2,
            explanation: 'Early CPR is crucial because it maintains blood flow to the brain and heart until more advanced interventions can be provided.'
          },
          {
            id: '2.5.3',
            question: 'What is the purpose of post-resuscitation care?',
            options: [
              'To complete paperwork',
              'To identify who to blame',
              'To plan future training',
              'To improve long-term survival and minimize complications'
            ],
            correctAnswer: 3,
            explanation: 'Post-resuscitation care is vital for improving long-term survival and minimizing complications through advanced interventions, medications, and treatment of underlying causes.'
          }
        ]
      }
    }
  },
  {
    id: '2.6',
    title: 'Personal Safety During Resuscitation',
    description: 'Understanding safety protocols and infection control during resuscitation',
    duration: 25,
    type: 'Lesson',
    content: {
      introduction: 'Ensuring personal safety during resuscitation is paramount to protect both the rescuer and the patient. This involves stringent infection prevention and control measures, effective hand hygiene, appropriate use of personal protective equipment (PPE), and proper cleaning and waste disposal.',
      objectives: [
        'Understand infection prevention measures',
        'Learn proper PPE usage',
        'Master safety protocols during resuscitation',
        'Understand cleaning and waste disposal procedures'
      ],
      mainContent: {
        infectionControl: {
          title: 'Infection Prevention and Control',
          content: 'Before initiating resuscitation, conduct a quick risk assessment to identify potential infectious risks and determine the necessary protective measures.',
          steps: [
            {
              name: 'Hand Hygiene',
              description: 'Perform hand washing with soap and water for at least 20 seconds before and after resuscitation.',
              details: 'If soap and water are not available, use an alcohol-based hand sanitizer containing at least 60% alcohol.',
              color: '#4caf50'
            },
            {
              name: 'Personal Protective Equipment',
              description: 'Wear disposable gloves to protect your hands from bodily fluids and contaminants.',
              details: 'Use a face mask or a surgical mask to protect against respiratory droplets. Wear safety goggles or a face shield to prevent splashes of bodily fluids into the eyes.',
              color: '#2196f3'
            },
            {
              name: 'Protective Clothing',
              description: 'Use disposable gowns or aprons to protect your clothing and skin from contaminants.',
              color: '#9c27b0'
            },
            {
              name: 'Equipment Care',
              description: 'Clean and disinfect all resuscitation equipment, such as AEDs and CPR manikins, immediately after use.',
              color: '#ff9800'
            },
            {
              name: 'Waste Management',
              description: 'Dispose of all contaminated items, including gloves, masks, and gowns, in designated biohazard waste containers.',
              color: '#f44336'
            }
          ]
        },
        safetyChecklist: {
          title: 'Safety Checklist During Resuscitation',
          content: 'Follow these key safety measures during resuscitation:',
          checkpoints: [
            {
              point: 'Scene Assessment',
              description: 'Evaluate the environment for potential hazards before beginning resuscitation.'
            },
            {
              point: 'Adequate Lighting',
              description: 'Ensure proper visibility to perform procedures safely.'
            },
            {
              point: 'PPE Verification',
              description: 'Double-check that all necessary protective equipment is worn correctly.'
            },
            {
              point: 'Consciousness and Safety Check',
              description: 'Assess patient consciousness while maintaining personal safety.'
            },
            {
              point: 'Early Assistance',
              description: 'Request help early to ensure adequate support and resources.'
            },
            {
              point: 'Patient Positioning',
              description: 'Use proper positioning and restraints when necessary.'
            },
            {
              point: 'Movement Control',
              description: 'Minimize unnecessary patient movement during procedures.'
            },
            {
              point: 'Electrical Safety',
              description: 'Maintain proper electrical safety protocols, especially when using AEDs.'
            },
            {
              point: 'Clear Communication',
              description: 'Maintain clear and effective communication with team members.'
            },
            {
              point: 'Back Safety',
              description: 'Use proper body mechanics to prevent personal injury.'
            },
            {
              point: 'Patient Monitoring',
              description: 'Continuously monitor the patient\'s condition while maintaining safety measures.'
            }
          ]
        }
      },
      quiz: {
        title: 'Personal Safety Quiz',
        description: 'Test your understanding of safety protocols during resuscitation',
        questions: [
          {
            id: '2.6.1',
            question: 'What is the minimum recommended duration for hand washing before and after resuscitation?',
            options: [
              '10 seconds',
              '15 seconds',
              '20 seconds',
              '30 seconds'
            ],
            correctAnswer: 2,
            explanation: 'Hand washing should be performed for at least 20 seconds with soap and water before and after resuscitation to ensure proper decontamination.'
          },
          {
            id: '2.6.2',
            question: 'Which of the following is NOT a required piece of personal protective equipment during resuscitation?',
            options: [
              'Disposable gloves',
              'Steel-toed boots',
              'Face mask or surgical mask',
              'Safety goggles or face shield'
            ],
            correctAnswer: 1,
            explanation: 'While steel-toed boots are important in some medical settings, they are not part of the required PPE for resuscitation. The essential items include gloves, masks, and eye protection.'
          },
          {
            id: '2.6.3',
            question: 'What should be done with contaminated PPE after use?',
            options: [
              'Save it for later use',
              'Wash and reuse it',
              'Dispose in regular trash',
              'Dispose in designated biohazard waste containers'
            ],
            correctAnswer: 3,
            explanation: 'All contaminated items, including PPE, must be disposed of in designated biohazard waste containers to prevent cross-contamination and ensure proper handling of potentially infectious materials.'
          }
        ]
      }
    }
  }
];

export const module3_lessons = [
  {
    id: '3.2',
    title: 'Primary Survey (ABCDE Approach)',
    description: 'Systematic approach to patient assessment',
    duration: 30,
    type: 'Interactive Lesson',
    content: {
      introduction: 'The ABCDE approach is a systematic method used during the primary survey of a patient to identify and manage life-threatening conditions promptly. This approach ensures that the most critical issues are addressed first, maximising the chances of survival and recovery.',
      objectives: [
        'Understand each component of ABCDE',
        'Learn systematic assessment techniques',
        'Practice the primary survey approach',
        'Identify life-threatening conditions quickly'
      ],
      mainContent: {
        primarySurvey: {
          title: 'Primary Survey (ABCDE Approach)',
          description: 'A systematic approach to assess and manage immediately life-threatening conditions',
          image: '/images/primaryabcde/1.png',
          imageAlt: 'ABCDE Primary Survey Approach Diagram',
          imageCaption: 'Systematic ABCDE approach for patient assessment',
          steps: [
            {
              letter: 'A',
              title: 'Airway',
              description: 'Assess and ensure airway patency',
              importance: 'Critical - Address immediately',
              checkPoints: [
                'Check if the airway is open and clear',
                'Look for visible obstructions',
                'Listen for abnormal airway sounds',
                'Remove any foreign bodies if visible',
                'Position the head appropriately'
              ],
              warningSign: 'Gurgling, stridor, or complete silence',
              icon: 'AccessibilityNew',
              color: '#d32f2f'
            },
            {
              letter: 'B',
              title: 'Breathing',
              description: 'Evaluate respiratory function',
              importance: 'Critical - Check within seconds',
              checkPoints: [
                'Observe chest movement',
                'Count respiratory rate',
                'Assess breathing depth and pattern',
                'Listen to breath sounds',
                'Check oxygen saturation if available'
              ],
              warningSign: 'Rapid, shallow, or labored breathing',
              icon: 'Air',
              color: '#f57c00'
            },
            {
              letter: 'C',
              title: 'Circulation',
              description: 'Assess cardiovascular status',
              importance: 'Critical - Evaluate quickly',
              checkPoints: [
                'Check pulse rate and quality',
                'Assess skin color and temperature',
                'Measure capillary refill time',
                'Look for external bleeding',
                'Monitor blood pressure if possible'
              ],
              warningSign: 'Weak pulse, cold extremities, delayed capillary refill',
              icon: 'MonitorHeart',
              color: '#2e7d32'
            },
            {
              letter: 'D',
              title: 'Disability',
              description: 'Check neurological status',
              importance: 'High Priority',
              checkPoints: [
                'Apply AVPU scale assessment',
                'Check pupil size and reaction',
                'Assess limb movement',
                'Test blood glucose level',
                'Look for signs of stroke'
              ],
              warningSign: 'Decreased consciousness, unequal pupils',
              icon: 'Psychology',
              color: '#6a1b9a'
            },
            {
              letter: 'E',
              title: 'Exposure',
              description: 'Complete physical examination',
              importance: 'High Priority',
              checkPoints: [
                'Expose body areas as needed',
                'Look for injuries or rashes',
                'Check body temperature',
                'Maintain patient dignity',
                'Protect from environmental conditions'
              ],
              warningSign: 'Unusual marks, bleeding, or temperature extremes',
              icon: 'Preview',
              color: '#1565c0'
            }
          ]
        }
      },
      quiz: {
        title: 'ABCDE Approach Assessment',
        description: 'Test your understanding of the Primary Survey',
        questions: [
          {
            id: 'abcde-1',
            question: 'What is the first step in the ABCDE approach?',
            options: [
              'Check breathing',
              'Assess airway',
              'Monitor circulation',
              'Check disability'
            ],
            correctAnswer: 1,
            explanation: 'The ABCDE approach always starts with Airway assessment as this is the most immediately life-threatening aspect.'
          },
          {
            id: 'abcde-2',
            question: 'Which of these is NOT a proper assessment under "B - Breathing"?',
            options: [
              'Checking respiratory rate',
              'Observing chest movement',
              'Measuring blood glucose',
              'Listening to breath sounds'
            ],
            correctAnswer: 2,
            explanation: 'Blood glucose measurement is part of the "D - Disability" assessment, not breathing assessment.'
          },
          {
            id: 'abcde-3',
            question: 'What does the AVPU scale assess?',
            options: [
              'Airway visibility',
              'Blood pressure',
              'Level of consciousness',
              'Breathing rate'
            ],
            correctAnswer: 2,
            explanation: 'The AVPU scale (Alert, Voice, Pain, Unresponsive) is used to assess the patient\'s level of consciousness under "D - Disability".'
          },
          {
            id: 'abcde-4',
            question: 'Which sign should be checked under "C - Circulation"?',
            options: [
              'Pupil reaction',
              'Capillary refill time',
              'Breathing rate',
              'Body temperature'
            ],
            correctAnswer: 1,
            explanation: 'Capillary refill time is a key circulation check that helps assess peripheral perfusion.'
          },
          {
            id: 'abcde-5',
            question: 'What is the primary purpose of "E - Exposure"?',
            options: [
              'To check vital signs',
              'To complete documentation',
              'To perform a full body examination',
              'To assess consciousness'
            ],
            correctAnswer: 2,
            explanation: 'Exposure allows for a complete physical examination while maintaining patient dignity and protecting them from environmental conditions.'
          }
        ]
      }
    }
  },
  {
    id: '3.3',
    title: 'Signs and Symptoms of Cardiac Arrest',
    description: 'Identifying cardiac arrest indicators',
    duration: 20,
    type: 'Interactive Lesson',
    content: {
      introduction: 'Recognizing the signs and symptoms of cardiac arrest is crucial for immediate response. Early recognition can significantly improve survival chances.',
      objectives: [
        'Identify key signs of cardiac arrest',
        'Understand the importance of early recognition',
        'Learn about preceding symptoms',
        'Recognize warning signs that may precede cardiac arrest'
      ],
      mainContent: {
        cardiacArrestSigns: {
          title: 'Signs and Symptoms of Cardiac Arrest',
          description: 'Cardiac arrest occurs suddenly and requires immediate action. Here are the main signs to watch for:',
          symptoms: [
            {
              title: 'Loss of Consciousness',
              description: 'Individuals undergoing a cardiac arrest will lose consciousness abruptly due to the lack of blood flow to the brain. They won\'t respond to stimuli and will appear lifeless.',
              severity: 'Critical',
              icon: 'Person',
              color: '#d32f2f',
              timeFrame: 'Immediate'
            },
            {
              title: 'Absence of Breathing',
              description: 'They will not be breathing or may have agonal gasps, which are sporadic, shallow, or weak breaths that can often be mistaken for normal breathing but are a sign of cardiac arrest.',
              severity: 'Critical',
              icon: 'Air',
              color: '#c2185b',
              timeFrame: 'Immediate'
            },
            {
              title: 'Absence of Pulse',
              description: 'There will be no detectable pulse.',
              severity: 'Critical',
              icon: 'MonitorHeart',
              color: '#7b1fa2',
              timeFrame: 'Immediate'
            },
            {
              title: 'Skin Color Changes',
              description: 'Due to the lack of circulation, the skin may become pale, ashy, or take on a bluish tint, particularly around the lips and fingertips.',
              severity: 'Severe',
              icon: 'Colorize',
              color: '#512da8',
              timeFrame: 'Within seconds'
            },
            {
              title: 'Pupil Dilation',
              description: 'The pupils may become dilated as a result of reduced blood flow to the brain.',
              severity: 'Severe',
              icon: 'Visibility',
              color: '#303f9f',
              timeFrame: 'Within minutes'
            },
            {
              title: 'Lack of Movement or Convulsions',
              description: 'There might be a lack of movement or, in some instances, convulsive-like activity.',
              severity: 'Severe',
              icon: 'AccessibilityNew',
              color: '#1976d2',
              timeFrame: 'Immediate'
            }
          ],
          precedingSymptoms: [
            {
              title: 'Chest Pain or Discomfort',
              description: 'Some individuals might experience chest pain, discomfort, or other symptoms indicative of a heart attack before cardiac arrest occurs.',
              warning: true,
              icon: 'HealthAndSafety',
              color: '#0097a7'
            },
            {
              title: 'Other Warning Signs',
              description: 'There may be other symptoms before the arrest like fatigue, fainting, blackouts, dizziness, shortness of breath, or weakness',
              warning: true,
              icon: 'ReportProblem',
              color: '#00796b'
            }
          ]
        }
      },
      quiz: {
        title: 'Test Your Knowledge',
        description: 'Test your understanding of cardiac arrest signs and symptoms',
        questions: [
          {
            id: 'cardiac-arrest-1',
            question: 'Which of these is NOT an immediate sign of cardiac arrest?',
            options: [
              'Loss of consciousness',
              'Absence of breathing',
              'Gradual skin color change over hours',
              'Absence of pulse'
            ],
            correctAnswer: 2,
            explanation: 'In cardiac arrest, skin color changes occur rapidly (within seconds to minutes) due to lack of circulation. Gradual changes over hours are not typical of sudden cardiac arrest.'
          },
          {
            id: 'cardiac-arrest-2',
            question: 'What type of breathing might be present during cardiac arrest that could be mistaken for normal breathing?',
            options: [
              'Deep regular breaths',
              'Agonal gasps',
              'Rapid shallow breathing',
              'Normal breathing pattern'
            ],
            correctAnswer: 1,
            explanation: 'Agonal gasps are sporadic, shallow breaths that may occur during cardiac arrest and can be mistaken for normal breathing. They are actually a sign of cardiac arrest.'
          },
          {
            id: 'cardiac-arrest-3',
            question: 'Which symptom might appear BEFORE cardiac arrest occurs?',
            options: [
              'Complete loss of consciousness',
              'Absence of pulse',
              'Chest pain or discomfort',
              'Fully dilated pupils'
            ],
            correctAnswer: 2,
            explanation: 'Chest pain or discomfort is a common warning sign that may precede cardiac arrest, often indicating a heart attack that could lead to cardiac arrest.'
          }
        ]
      }
    }
  },
  {
    id: '3.4',
    title: 'How to Recognize Cardiac Arrest',
    description: 'Practical guide to cardiac arrest recognition',
    duration: 25,
    type: 'Interactive Lesson',
    content: {
      introduction: 'Quick and accurate recognition of cardiac arrest is crucial for survival. This lesson provides practical steps and guidelines for identifying cardiac arrest and initiating an immediate response.',
      objectives: [
        'Identify the key indicators of cardiac arrest',
        'Learn the step-by-step approach to recognition',
        'Understand emergency response protocols',
        'Practice recognition through scenarios'
      ],
      mainContent: {
        keyIndicators: {
          title: 'Key Indicators of Cardiac Arrest',
          description: 'Learn to recognize these critical signs that indicate cardiac arrest:',
          indicators: [
            {
              title: 'Sudden Collapse',
              description: 'The individual may suddenly fall to the ground without warning. This collapse is typically abrupt and may occur while the person is engaged in normal activities.',
              icon: 'PersonOff',
              severity: 'Critical',
              color: '#d32f2f'
            },
            {
              title: 'Unresponsiveness',
              description: 'The person does not respond to verbal commands, tapping on the shoulders, or any form of stimulation.',
              icon: 'NotInterested',
              severity: 'Critical',
              color: '#c2185b'
            },
            {
              title: 'Absence of Normal Breathing',
              description: 'The person is not breathing or is only gasping (agonal respirations). Gasping breaths are irregular, weak attempts at breathing that are ineffective for oxygenation. Normal breathing is absent.',
              icon: 'AirOff',
              severity: 'Critical',
              color: '#7b1fa2'
            },
            {
              title: 'No Pulse',
              description: 'There is no detectable pulse. Checking for a pulse in an emergency setting can be challenging and time-consuming, so the absence of breathing and responsiveness is often used as a primary indicator.',
              icon: 'MonitorHeart',
              severity: 'Critical',
              color: '#512da8'
            }
          ]
        },
        recognitionSteps: {
          title: 'Steps to Recognize Cardiac Arrest',
          description: 'Follow these steps systematically to recognize cardiac arrest:',
          steps: [
            {
              step: 1,
              title: 'Approach Safely',
              description: 'Ensure the scene is safe before approaching the person. Look out for potential hazards such as falling debris, electrical wires, or machinery.',
              icon: 'Security',
              color: '#1976d2'
            },
            {
              step: 2,
              title: 'Check for Responsiveness',
              description: 'Tap the person\'s shoulders firmly and shout, "Are you okay?" Observe for any signs of movement or response.',
              icon: 'Help',
              color: '#0097a7'
            },
            {
              step: 3,
              title: 'Assess Breathing',
              description: 'Look for normal chest movements, listen for breath sounds, and feel for breath on your cheek for no more than 10 seconds. Agonal breaths (irregular gasps) are not considered normal breathing.',
              icon: 'Air',
              color: '#00796b'
            },
            {
              step: 4,
              title: 'Call for Help',
              description: 'If the person is unresponsive and not breathing normally, immediately call emergency services. Clearly state that you suspect cardiac arrest and provide the location.',
              icon: 'Call',
              color: '#e53935'
            }
          ]
        },
        emergencyResponse: {
          title: 'Emergency Response Protocols',
          description: 'Follow these protocols based on the situation:',
          protocols: [
            {
              scenario: 'Alone with a mobile phone',
              action: 'Dial emergency services (999 in the UK), activate the speaker option, and simultaneously initiate CPR with guidance from the dispatcher.',
              icon: 'Phone',
              color: '#2196f3'
            },
            {
              scenario: 'Lone rescuer',
              action: 'If you have to leave a victim to ring the ambulance service, alert the ambulance service first and then start CPR.',
              icon: 'Person',
              color: '#3f51b5'
            },
            {
              scenario: 'Prepare for CPR',
              action: 'If there is no response and the person is not breathing or only gasping, begin CPR immediately. Send someone to fetch an automated external defibrillator (AED) if available.',
              icon: 'Defibrillator',
              color: '#f44336'
            }
          ]
        }
      },
      quiz: {
        title: 'Recognition Skills Assessment',
        description: 'Test your understanding of cardiac arrest recognition',
        questions: [
          {
            id: 'recognize-arrest-1',
            question: 'What is the maximum time you should spend checking for breathing?',
            options: [
              '5 seconds',
              '10 seconds',
              '15 seconds',
              '20 seconds'
            ],
            correctAnswer: 1,
            explanation: 'You should spend no more than 10 seconds checking for breathing. Taking longer could delay critical interventions.'
          },
          {
            id: 'recognize-arrest-2',
            question: 'Which of these is NOT a key indicator of cardiac arrest?',
            options: [
              'Sudden collapse',
              'Mild chest pain',
              'Absence of normal breathing',
              'Unresponsiveness'
            ],
            correctAnswer: 1,
            explanation: 'While chest pain can precede cardiac arrest, it is not a key indicator of cardiac arrest itself. The key indicators are sudden collapse, unresponsiveness, and absence of normal breathing.'
          },
          {
            id: 'recognize-arrest-3',
            question: 'What should you do first when approaching a potential cardiac arrest victim?',
            options: [
              'Start chest compressions',
              'Check for breathing',
              'Ensure scene safety',
              'Call emergency services'
            ],
            correctAnswer: 2,
            explanation: 'Always ensure the scene is safe before approaching the victim to prevent becoming a victim yourself.'
          }
        ]
      }
    }
  },
 
];

export const module4_lessons = [
  {
    id: '4.2',
    title: 'Steps to Perform the Primary Survey',
    description: 'Detailed guide to primary survey steps',
    duration: 25,
    type: 'Interactive Lesson',
    content: {
      introduction: 'The primary survey is a systematic approach to quickly assess and address life-threatening conditions in an emergency situation. This structured method ensures that critical issues are identified and managed in order of priority, maximizing the chance of survival.',
      objectives: [
        'Master the D R H A B C approach to primary survey',
        'Learn to identify and respond to life-threatening conditions',
        'Understand when and how to initiate CPR',
        'Practice proper assessment techniques for airway, breathing, and circulation'
      ],
      mainContent: {
        drsabcdApproach: {
          title: 'The D R H A B C Approach',
          description: 'The primary survey follows the DR H ABC sequence – a systematic approach to assess and manage life-threatening conditions, especially in trauma situations:',
          lessonImages: [
            {
              src: '/images/stepstoperformtheprimarysurvery/step1.png',
              alt: 'Checking for Danger',
              caption: 'Step 1: Assess the scene for potential dangers'
            },
            {
              src: '/images/stepstoperformtheprimarysurvery/step2.png',
              alt: 'Checking Response',
              caption: 'Step 2: Check if the patient is responsive'
            },
            {
              src: '/images/stepstoperformtheprimarysurvery/step3.png',
              alt: 'Calling for Help',
              caption: 'Step 3: Call for emergency assistance'
            },
            {
              src: '/images/stepstoperformtheprimarysurvery/step4.png',
              alt: 'Checking Airway',
              caption: 'Step 4: Ensure airway is open with cervical spine protection'
            },
            {
              src: '/images/stepstoperformtheprimarysurvery/step5.png',
              alt: 'Checking Breathing and Circulation',
              caption: 'Step 5: Assess breathing and circulation'
            },
            {
              src: '/images/stepstoperformtheprimarysurvery/step6.png',
              alt: 'Recovery Position',
              caption: 'Step 6: Place patient in recovery position if breathing'
            },
            {
              src: '/images/stepstoperformtheprimarysurvery/step7.png',
              alt: 'CPR Preparation',
              caption: 'Step 7: Prepare for CPR if not breathing normally'
            },
            {
              src: '/images/stepstoperformtheprimarysurvery/step8.png',
              alt: 'Ongoing Assessment',
              caption: 'Step 8: Continue assessment until emergency services arrive'
            }
          ],
          steps: [
            {
              letter: 'D',
              title: 'Danger',
              description: 'Check for dangers to yourself, bystanders, and the patient',
              actions: [
                'Scan the environment for hazards (electrical, chemical, physical)',
                'Remove dangers if safe to do so',
                'If dangers cannot be removed, move the patient to safety if possible',
                'Never put yourself at risk – you cannot help if you become a victim'
              ],
              icon: 'Warning',
              color: '#d32f2f'
            },
            {
              letter: 'R',
              title: 'Response',
              description: 'Check if the patient is responsive',
              actions: [
                'Gently shake shoulders and ask loudly: "Are you alright?"',
                'Note any response (verbal, movement, eye opening)',
                'If responsive, maintain position and assess for injuries',
                'If unresponsive, proceed to next step'
              ],
              icon: 'RecordVoiceOver',
              color: '#ff9800'
            },
            {
              letter: 'H',
              title: 'Help',
              description: 'Call for emergency assistance immediately',
              actions: [
                'Call emergency services or ask someone else to do so',
                'State the location, nature of emergency, and number of patients',
                'Stay on the line until instructed to hang up',
                'If possible, send someone to guide responders to the scene'
              ],
              icon: 'Call',
              color: '#1976d2'
            },
            {
              letter: 'A',
              title: 'Airway with Cervical Spine Protection',
              description: 'Ensure the airway is open while protecting the cervical spine',
              actions: [
                'Avoid moving the neck if spinal injury is suspected',
                'Use jaw thrust technique instead of head tilt-chin lift',
                'Clear visible obstructions from the airway',
                'Place airway adjuncts if trained and available'
              ],
              icon: 'Air',
              color: '#4caf50'
            },
            {
              letter: 'B',
              title: 'Breathing',
              description: 'Assess if the patient is breathing normally',
              actions: [
                'Look, listen, and feel for breathing for up to 10 seconds',
                'Observe chest rise and fall',
                'Administer rescue breaths if not breathing and trained to do so',
                'Prepare to start CPR if breathing is absent or abnormal'
              ],
              icon: 'Lungs',
              color: '#9c27b0'
            },
            {
              letter: 'C',
              title: 'Circulation with Haemorrhage Control',
              description: 'Assess circulation and control major bleeding',
              actions: [
                'Check for severe external bleeding and control it immediately',
                'Apply direct pressure or use tourniquets if trained',
                'Look for signs of shock (pale, cold, clammy skin, fast pulse)',
                'Continue to monitor circulation throughout the response'
              ],
              icon: 'Favorite',
              color: '#e91e63'
            }
          ]
        }
,        
        assessmentTechniques: {
          title: 'Key Assessment Techniques',
          techniques: [
            {
              title: 'Head Tilt-Chin Lift',
              description: 'The proper technique to open the airway in a non-trauma patient:',
              steps: [
                'Place one hand on the forehead',
                'Place fingertips of other hand under the bony part of the chin',
                'Tilt the head back gently while lifting the chin',
                'Avoid pressing on the soft tissues under the chin'
              ],
              image: 'head-tilt-chin-lift',
              icon: 'Face'
            },
            {
              title: 'Jaw Thrust',
              description: 'Used when spinal injury is suspected:',
              steps: [
                'Kneel at the top of the patient\'s head',
                'Rest your elbows on the surface where the patient is lying',
                'Place fingers at the angles of the patient\'s jaw',
                'Lift the jaw forward without tilting the head',
                'This opens the airway while maintaining spinal alignment'
              ],
              image: 'jaw-thrust',
              icon: 'Face'
            },
            {
              title: 'Look-Listen-Feel',
              description: 'The proper technique to assess breathing:',
              steps: [
                'Position your face close to the patient\'s mouth and nose',
                'Look for chest rise and fall',
                'Listen for breath sounds',
                'Feel for air on your cheek',
                'Take no more than 10 seconds to determine if breathing is normal'
              ],
              icon: 'Visibility'
            }
          ]
        },
        criticalDecisions: {
          title: 'Critical Decision Points',
          decisions: [
            {
              scenario: 'Patient is unresponsive but breathing normally',
              action: 'Place in recovery position, monitor breathing continuously, and wait for emergency services',
              icon: 'PersonOutline',
              color: '#4caf50'
            },
            {
              scenario: 'Patient is unresponsive and not breathing normally',
              action: 'Begin CPR immediately starting with chest compressions',
              icon: 'Favorite',
              color: '#f44336'
            },
            {
              scenario: 'Patient has life-threatening bleeding',
              action: 'Control bleeding with direct pressure before proceeding with other steps',
              icon: 'BloodtypeOutlined',
              color: '#d32f2f'
            },
            {
              scenario: 'Patient shows signs of airway obstruction',
              action: 'Attempt to clear airway; if conscious, encourage coughing; if ineffective, perform back blows and abdominal thrusts',
              icon: 'Air',
              color: '#ff9800'
            }
          ]
        }
      },
      quiz: {
        title: 'Primary Survey Assessment',
        description: 'Test your understanding of the primary survey steps',
        questions: [
          {
            id: 'primary-survey-1',
            question: 'What is the correct sequence for the primary survey?',
            options: [
              'ABCDE',
              'DRSABCD',
              'SAMPLE',
              'FAST'
            ],
            correctAnswer: 1,
            explanation: 'The correct sequence is DRSABCD: Danger, Response, Send for help, Airway, Breathing, Circulation, Defibrillation.'
          },
          {
            id: 'primary-survey-2',
            question: 'When checking for breathing in an unresponsive patient, how long should you take?',
            options: [
              'Up to 5 seconds',
              'Up to 10 seconds',
              'Up to 30 seconds',
              'As long as needed'
            ],
            correctAnswer: 1,
            explanation: 'You should take no more than 10 seconds to check for normal breathing. This prevents unnecessary delay in starting CPR if needed.'
          },
          {
            id: 'primary-survey-3',
            question: 'Which technique should be used to open the airway when spinal injury is suspected?',
            options: [
              'Head tilt-chin lift',
              'Jaw thrust',
              'Neck extension',
              'Chin depression'
            ],
            correctAnswer: 1,
            explanation: 'The jaw thrust technique should be used when spinal injury is suspected as it maintains spinal alignment while opening the airway.'
          },
          {
            id: 'primary-survey-4',
            question: 'What is the first step in the primary survey?',
            options: [
              'Check for response',
              'Open the airway',
              'Check for dangers',
              'Call for help'
            ],
            correctAnswer: 2,
            explanation: 'The first step is to check for dangers to ensure the safety of yourself, bystanders, and the patient.'
          }
        ]
      }
    }
  },
  {
    id: '4.3',
    title: 'High Quality Chest Compressions',
    description: 'Mastering chest compression techniques',
    duration: 30,
    type: 'Lesson',
    content: {
      introduction: 'High-quality chest compressions are the foundation of effective CPR. When performed correctly, chest compressions maintain blood flow to vital organs, significantly increasing the chance of survival during cardiac arrest. This lesson covers the essential techniques and parameters for delivering effective chest compressions.',
      objectives: [
        'Master proper hand position and body mechanics',
        'Achieve correct compression depth and rate',
        'Understand the importance of complete chest recoil',
        'Learn how to minimize interruptions during compressions'
      ],
      mainContent: {
        compressionBasics: {
          title: 'Chest Compression Fundamentals',
          description: 'Effective chest compressions require proper technique and attention to key parameters:',
          keyComponents: [
            {
              component: 'Hand Position',
              description: 'Place the heel of one hand on the center of the chest (lower half of the sternum). Place your other hand on top of the first hand, interlacing your fingers. Keep your fingers off the ribs.'
            },
            {
              component: 'Body Position',
              description: 'Position yourself directly over the victim\'s chest with your shoulders above your hands. Keep your arms straight and elbows locked. This position allows you to use your body weight effectively.'
            },
            {
              component: 'Compression Depth',
              description: 'Compress the chest at least 2 inches (5-6 cm) for an average adult. The depth should be sufficient to generate blood flow but avoid excessive force that could cause injury.'
            },
            {
              component: 'Compression Rate',
              description: 'Maintain a rate of 100-120 compressions per minute. This pace is fast enough to generate adequate blood flow but allows sufficient time for complete chest recoil between compressions.'
            },
            {
              component: 'Chest Recoil',
              description: 'Allow complete chest recoil after each compression. This enables the heart to refill with blood between compressions, improving the effectiveness of the next compression.'
            },
            {
              component: 'Minimize Interruptions',
              description: 'Limit pauses in chest compressions to less than 10 seconds. Continuous compressions maintain blood flow to vital organs.'
            }
          ]
        },
        commonErrors: {
          title: 'Common Errors to Avoid',
          description: 'Being aware of these common mistakes can help improve the quality of chest compressions:',
          errors: [
            {
              error: 'Insufficient Depth',
              description: 'Compressions that are too shallow fail to create adequate blood flow. Ensure you compress to at least 2 inches (5-6 cm) depth for adults.',
              correction: 'Position yourself directly over the chest and use your body weight. Don\'t be afraid to push hard enough.'
            },
            {
              error: 'Inadequate Rate',
              description: 'Compressions that are too slow or too fast reduce effectiveness. Too slow doesn\'t generate sufficient flow, while too fast doesn\'t allow proper filling time.',
              correction: 'Aim for 100-120 compressions per minute. This is about the tempo of the song "Stayin\' Alive" by the Bee Gees.'
            },
            {
              error: 'Incomplete Recoil',
              description: 'Leaning on the chest between compressions prevents full chest expansion and reduces blood return to the heart.',
              correction: 'Completely release pressure after each compression, but maintain contact with the chest.'
            },
            {
              error: 'Incorrect Hand Position',
              description: 'Placing hands too low (on the xiphoid process) or too high (on the upper sternum) reduces effectiveness and increases risk of injury.',
              correction: 'Place hands on the lower half of the sternum, in the center of the chest between the nipples.'
            },
            {
              error: 'Frequent Interruptions',
              description: 'Every pause in compressions results in a drop in coronary perfusion pressure.',
              correction: 'Minimize interruptions to less than 10 seconds. Plan and communicate before stopping compressions.'
            }
          ]
        },
        rescuerFatigue: {
          title: 'Managing Rescuer Fatigue',
          description: 'Performing high-quality chest compressions is physically demanding. Fatigue can quickly lead to decreased compression quality:',
          strategies: [
            {
              strategy: 'Rotate Rescuers',
              description: 'Switch compressors every 2 minutes (or about 5 cycles of 30:2) to maintain compression quality. Plan the switch during planned pauses to minimize interruptions.'
            },
            {
              strategy: 'Proper Body Mechanics',
              description: 'Use your body weight rather than arm strength. Keep your arms straight and shoulders directly over your hands to reduce fatigue.'
            },
            {
              strategy: 'Team Communication',
              description: 'Clearly communicate when you need to switch. A simple "I need to switch" alert gives your team time to prepare for a smooth transition.'
            },
            {
              strategy: 'Self-Assessment',
              description: 'Monitor your own performance. If you notice your compressions becoming less effective, request a switch even if it hasn\'t been 2 minutes.'
            }
          ]
        },
        specialConsiderations: {
          title: 'Special Considerations',
          considerations: [
            {
              situation: 'Pregnant Patients',
              guidelines: [
                'Position the patient on a firm, flat surface',
                'Place a wedge under the right hip to displace the uterus to the left',
                'Hand position remains the same as for non-pregnant adults',
                'Maintain the same depth and rate parameters'
              ]
            },
            {
              situation: 'Elderly Patients',
              guidelines: [
                'Be aware of increased risk of rib fractures',
                'Maintain standard depth and rate parameters despite this risk',
                'The benefit of effective compressions outweighs the risk of fractures'
              ]
            },
            {
              situation: 'Obese Patients',
              guidelines: [
                'May require additional force to achieve adequate depth',
                'More frequent rotation of compressors may be necessary due to increased effort',
                'Ensure the surface under the patient is firm enough to allow effective compressions'
              ]
            }
          ]
        }
      },
      quiz: {
        title: 'Chest Compression Knowledge Check',
        description: 'Test your understanding of high-quality chest compressions',
        questions: [
          {
            id: 'chest-compression-1',
            question: 'What is the correct depth for chest compressions in adults?',
            options: [
              '1-2 inches (3-4 cm)',
              'At least 2 inches (5-6 cm)',
              '3-4 inches (7-10 cm)',
              'As deep as possible'
            ],
            correctAnswer: 1,
            explanation: 'Chest compressions should be at least 2 inches (5-6 cm) deep for adults. This depth is necessary to create sufficient blood flow while minimizing the risk of injury.'
          },
          {
            id: 'chest-compression-2',
            question: 'What is the recommended rate for chest compressions?',
            options: [
              '60-80 compressions per minute',
              '80-100 compressions per minute',
              '100-120 compressions per minute',
              '120-140 compressions per minute'
            ],
            correctAnswer: 2,
            explanation: 'The recommended rate is 100-120 compressions per minute. This rate balances the need for adequate blood flow with allowing sufficient time for chest recoil.'
          },
          {
            id: 'chest-compression-3',
            question: 'Where should you position your hands during chest compressions?',
            options: [
              'On the left side of the chest, over the heart',
              'On the center of the chest, on the lower half of the sternum',
              'On the upper part of the sternum',
              'Just below the rib cage'
            ],
            correctAnswer: 1,
            explanation: 'Hand position should be on the center of the chest, on the lower half of the sternum. This position allows for effective compression of the heart against the spine.'
          },
          {
            id: 'chest-compression-4',
            question: 'How often should rescuers switch roles during CPR to prevent fatigue?',
            options: [
              'Every 1 minute',
              'Every 2 minutes',
              'Every 5 minutes',
              'Only when the rescuer feels tired'
            ],
            correctAnswer: 1,
            explanation: 'Rescuers should switch approximately every 2 minutes to prevent fatigue from compromising compression quality. This typically corresponds to about 5 cycles of 30:2 compressions to breaths.'
          }
        ]
      }
    }
  },
  {
    id: '4.4',
    title: 'Rescue Breaths',
    description: 'Proper rescue breathing techniques',
    duration: 25,
    type: 'Interactive Lesson',
    content: {
      introduction: 'Rescue breaths are a critical component of cardiopulmonary resuscitation (CPR), providing oxygen to the lungs of a person who is not breathing or not breathing adequately. When performed correctly, rescue breaths can help maintain oxygenation of vital organs until advanced medical care arrives. This lesson covers the proper techniques for delivering effective rescue breaths.',
      objectives: [
        'Master proper head positioning and airway opening techniques',
        'Learn correct rescue breath delivery methods',
        'Understand common challenges and solutions in airway management',
        'Practice proper ventilation techniques with and without barriers'
      ],
      mainContent: {
        airwayManagement: {
          title: 'Airway Management Fundamentals',
          description: 'Before delivering rescue breaths, ensuring an open airway is essential:',
          techniques: [
            {
              technique: 'Head Tilt-Chin Lift',
              description: 'The primary method for opening the airway in most patients:',
              steps: [
                'Place one hand on the victim\'s forehead and apply firm backward pressure to tilt the head back',
                'Place the fingertips of your other hand under the bony part of the lower jaw near the chin',
                'Lift the chin to bring the jaw forward and open the airway',
                'Avoid pressing deeply into the soft tissues under the chin, which might block the airway'
              ],
              considerations: 'This technique should not be used if cervical spine injury is suspected.'
            },
            {
              technique: 'Jaw Thrust',
              description: 'Alternative method when cervical spine injury is suspected:',
              steps: [
                'Kneel at the top of the victim\'s head',
                'Rest your elbows on the surface where the victim is lying',
                'Place your fingers at the angles of the victim\'s lower jaw and lift with both hands',
                'Push the jaw forward without tilting the head or neck'
              ],
              considerations: 'This technique is more difficult to perform and may require two rescuers.'
            },
            {
              technique: 'Checking for Obstructions',
              description: 'Before delivering breaths, quickly check for visible obstructions:',
              steps: [
                'Open the victim\'s mouth and look for obvious obstructions',
                'If visible and easily accessible, remove foreign objects with a finger sweep',
                'Do not perform blind finger sweeps as this may push obstructions deeper'
              ]
            }
          ]
        },
        breathDelivery: {
          title: 'Rescue Breath Delivery Technique',
          description: 'Proper technique ensures effective oxygen delivery:',
          lessonImages: [
            {
              src: '/images/rescuebreaths/1.png',
              alt: 'Rescue Breath Delivery Technique',
              caption: 'Proper technique for delivering rescue breaths'
            }
          ],
          steps: [
            {
              step: 'Position Yourself',
              description: 'Kneel beside the victim\'s shoulders, positioning yourself to easily access both the airway and chest.'
            },
            {
              step: 'Open the Airway',
              description: 'Use the head tilt-chin lift maneuver to open the airway (or jaw thrust if spinal injury is suspected).'
            },
            {
              step: 'Pinch the Nose',
              description: 'Use the hand that\'s on the forehead to pinch the soft part of the nose closed with your thumb and index finger.'
            },
            {
              step: 'Create a Seal',
              description: 'Take a normal breath (not a deep one), place your mouth over the victim\'s mouth creating a tight seal.'
            },
            {
              step: 'Deliver the Breath',
              description: 'Blow steadily into the victim\'s mouth for about 1 second, watching for the chest to rise. This indicates successful air delivery.'
            },
            {
              step: 'Allow Exhalation',
              description: 'Maintain the head tilt but remove your mouth, turning your head slightly to watch for the chest to fall as air escapes.'
            },
            {
              step: 'Repeat',
              description: 'Take another normal breath and deliver a second rescue breath following the same procedure.'
            }
          ],
          keyPoints: [
            'Each breath should last about 1 second',
            'Provide just enough air to make the chest visibly rise',
            'Avoid excessive ventilation (too much force or volume)',
            'Complete both breaths within a maximum of 10 seconds'
          ]
        },
        commonChallenges: {
          title: 'Common Challenges and Solutions',
          challenges: [
            {
              challenge: 'Chest Doesn\'t Rise',
              causes: [
                'Inadequate head tilt',
                'Incomplete seal around the mouth',
                'Not pinching the nose closed',
                'Airway obstruction'
              ],
              solutions: [
                'Reposition the head using proper head tilt-chin lift technique',
                'Ensure a proper seal around the mouth',
                'Check for and remove visible obstructions',
                'If unsuccessful after two attempts, proceed with chest compressions'
              ]
            },
            {
              challenge: 'Gastric Inflation',
              causes: [
                'Delivering breaths too forcefully',
                'Breathing too quickly',
                'Excessive volume of air'
              ],
              solutions: [
                'Use only enough air to make the chest rise',
                'Deliver breaths over 1 second',
                'Avoid taking deep breaths before delivering rescue breaths'
              ]
            },
            {
              challenge: 'Resistance to Airflow',
              causes: [
                'Airway not properly opened',
                'Foreign body obstruction',
                'Tongue blocking the airway'
              ],
              solutions: [
                'Recheck and adjust head tilt-chin lift position',
                'Consider jaw thrust if needed',
                'Check for visible obstructions in the mouth'
              ]
            }
          ]
        },
        barrierDevices: {
          title: 'Using Barrier Devices',
          description: 'Barrier devices provide protection while delivering rescue breaths:',
          devices: [
            {
              device: 'Face Shield',
              description: 'A thin, flexible barrier with a filter or opening for rescue breathing:',
              usage: [
                'Position the face shield over the victim\'s mouth and nose',
                'Ensure the breathing port is centered over the mouth',
                'Maintain a proper seal while delivering breaths through the port',
                'Continue to use head tilt-chin lift to maintain an open airway'
              ],
              advantages: 'Portable, inexpensive, and easy to use; some have one-way valves to prevent backflow'
            },
            {
              device: 'Pocket Mask',
              description: 'A clear plastic device that creates a tight seal over the mouth and nose:',
              usage: [
                'Position the mask over the victim\'s mouth and nose with the narrow end at the bridge of the nose',
                'Create a seal by pressing the edges of the mask firmly against the face',
                'If the mask has a one-way valve, deliver breaths through the valve',
                'Maintain head tilt-chin lift with one hand while holding the mask with the other'
              ],
              advantages: 'Better seal than face shields; some include oxygen ports for supplemental oxygen'
            }
          ],
          safetyNote: 'While barrier devices reduce the risk of disease transmission, proper hand hygiene and standard precautions should still be followed.'
        }
      },
      quiz: {
        title: 'Rescue Breathing Knowledge Check',
        description: 'Test your understanding of rescue breathing techniques',
        questions: [
          {
            id: 'rescue-breath-1',
            question: 'What is the proper duration for delivering a rescue breath?',
            options: [
              'As quickly as possible (less than 0.5 seconds)',
              'About 1 second',
              '2-3 seconds',
              'As long as needed until the chest rises'
            ],
            correctAnswer: 1,
            explanation: 'Each rescue breath should be delivered over about 1 second. This provides sufficient time for air delivery while preventing excessive force that could cause gastric inflation.'
          },
          {
            id: 'rescue-breath-2',
            question: 'What is the primary indicator that a rescue breath has been delivered effectively?',
            options: [
              'You feel resistance when blowing',
              'The victim\'s chest visibly rises',
              'You can hear air entering the lungs',
              'The victim\'s color improves immediately'
            ],
            correctAnswer: 1,
            explanation: 'The visible rise of the chest is the primary indicator that air is successfully entering the lungs. This visual confirmation is essential for effective rescue breathing.'
          },
          {
            id: 'rescue-breath-3',
            question: 'What should you do if the chest doesn\'t rise after your first attempt at a rescue breath?',
            options: [
              'Blow harder with more force',
              'Skip rescue breaths and continue with compressions only',
              'Reposition the head and try again',
              'Perform abdominal thrusts'
            ],
            correctAnswer: 2,
            explanation: 'If the chest doesn\'t rise, you should reposition the head using the head tilt-chin lift maneuver and attempt a second rescue breath. Proper positioning often resolves the issue.'
          },
          {
            id: 'rescue-breath-4',
            question: 'What is the maximum time that should be spent attempting to deliver two rescue breaths?',
            options: [
              '5 seconds',
              '10 seconds',
              '15 seconds',
              '20 seconds'
            ],
            correctAnswer: 1,
            explanation: 'You should spend no more than 10 seconds attempting to deliver two rescue breaths. Minimizing interruptions to chest compressions is critical for maintaining blood flow.'
          }
        ]
      }
    }
  },
  {
    id: '4.5',
    title: 'The Formula for CPR',
    description: 'Understanding CPR ratios and timing',
    duration: 20,
    type: 'Lesson',
    content: {
      introduction: 'Cardiopulmonary Resuscitation (CPR) follows specific formulas and ratios designed to optimize blood circulation and oxygenation during cardiac arrest. Understanding these standardized protocols ensures that rescuers deliver consistent, high-quality CPR that maximizes the victim\'s chances of survival. This lesson covers the essential formulas, ratios, and timing sequences for adult, child, and infant CPR.',
      objectives: [
        'Master compression-to-breath ratios for different age groups',
        'Learn proper timing for compressions and breaths',
        'Understand CPR cycle sequences and when to reassess',
        'Recognize how to adapt CPR formulas for different scenarios'
      ],
      mainContent: {
        cprRatios: {
          title: 'CPR Compression-to-Breath Ratios',
          description: 'The compression-to-breath ratio is a fundamental formula in CPR that defines how many chest compressions should be delivered relative to rescue breaths:',
          ageGroups: [
            {
              group: 'Adult CPR',
              ratio: '30:2',
              description: 'For adults (age 8+ years), deliver 30 chest compressions followed by 2 rescue breaths. This ratio applies whether one or two rescuers are performing CPR.',
              notes: 'If an advanced airway is in place, switch to continuous compressions with 1 breath every 6 seconds (10 breaths per minute).'
            },
            {
              group: 'Child CPR',
              ratio: '30:2 (single rescuer) or 15:2 (two rescuers)',
              description: 'For children (age 1-8 years), a single rescuer should deliver 30 compressions followed by 2 breaths. With two rescuers, switch to 15 compressions followed by 2 breaths.',
              notes: 'Children have higher respiratory needs relative to cardiac output compared to adults.'
            },
            {
              group: 'Infant CPR',
              ratio: '30:2 (single rescuer) or 15:2 (two rescuers)',
              description: 'For infants (under 1 year), a single rescuer should deliver 30 compressions followed by 2 breaths. With two rescuers, switch to 15 compressions followed by 2 breaths.',
              notes: 'Use two fingers or two thumbs-encircling hands technique for compressions.'
            },
            {
              group: 'Compression-Only CPR',
              ratio: 'Continuous compressions',
              description: 'For untrained rescuers or those unwilling/unable to deliver rescue breaths, continuous chest compressions without pausing for breaths is recommended.',
              notes: 'While not as effective as conventional CPR with breaths, compression-only CPR is still significantly better than no CPR.'
            }
          ]
        },
        compressionRate: {
          title: 'Compression Rate and Depth',
          description: 'The rate and depth of compressions are critical factors in effective CPR:',
          parameters: [
            {
              parameter: 'Compression Rate',
              adult: '100-120 compressions per minute',
              child: '100-120 compressions per minute',
              infant: '100-120 compressions per minute',
              description: 'Maintain a steady rhythm of 100-120 compressions per minute for all age groups. This is approximately 30 compressions delivered in 15-18 seconds.',
              tip: 'The beat of songs like "Stayin\' Alive" by the Bee Gees or "Baby Shark" matches the ideal compression rate.'
            },
            {
              parameter: 'Compression Depth',
              adult: 'At least 2 inches (5-6 cm)',
              child: 'About 2 inches (5 cm)',
              infant: 'About 1.5 inches (4 cm)',
              description: 'Compress the chest to the appropriate depth for the victim\'s age. Ensure complete chest recoil between compressions.',
              tip: 'Push hard and fast, allowing the chest to fully return to its normal position between compressions.'
            },
            {
              parameter: 'Compression Fraction',
              description: 'Chest Compression Fraction (CCF) refers to the proportion of time during a cardiac arrest in which chest compressions are performed.',
              target: 'At least 60% of the total resuscitation time, with a goal of exceeding 80%',
              importance: 'Minimizing interruptions in chest compressions is crucial for maintaining blood flow to vital organs.'
            }
          ]
        },
        cprCycles: {
          title: 'CPR Cycles and Reassessment',
          description: 'Understanding when to perform cycles and reassess the victim is essential:',
          cycles: [
            {
              title: 'Standard CPR Cycle',
              description: 'A standard CPR cycle consists of 30 compressions followed by 2 breaths (for adult single-rescuer CPR). Five cycles typically take about 2 minutes to complete.',
              keyPoints: [
                'Complete 5 cycles before reassessing the pulse if alone',
                'In two-rescuer CPR, switch positions every 2 minutes to prevent fatigue',
                'Minimize interruptions to chest compressions during switches'
              ]
            },
            {
              title: 'Pulse Check',
              description: 'Pulse checks should be brief (no more than 10 seconds) and performed at specific intervals:',
              when: [
                'Initially, before starting CPR',
                'After 2 minutes (approximately 5 cycles) of CPR',
                'When an AED advises no shock is needed',
                'When the victim shows signs of life'
              ]
            },
            {
              title: 'AED Integration',
              description: 'When an AED becomes available:',
              steps: [
                'Turn on the AED and follow voice/visual prompts',
                'Pause CPR only when analyzing rhythm and delivering shocks',
                'Resume CPR immediately after shock delivery or if no shock is advised',
                'Minimize the pause in chest compressions to less than 10 seconds'
              ]
            }
          ]
        },
        specialScenarios: {
          title: 'Special CPR Scenarios',
          description: 'The standard CPR formula may need to be adapted in certain situations:',
          scenarios: [
            {
              scenario: 'Pregnant Victims',
              adaptations: [
                'Position the victim with a left lateral tilt (manually displace the uterus to the left)',
                'Hand position for chest compressions may need to be slightly higher on the sternum',
                'Standard 30:2 ratio still applies'
              ]
            },
            {
              scenario: 'Drowning Victims',
              adaptations: [
                'Begin with 5 initial rescue breaths before starting compressions',
                'Continue with standard 30:2 ratio afterward',
                'Focus on effective ventilation alongside quality compressions'
              ]
            },
            {
              scenario: 'Hypothermia',
              adaptations: [
                'Check pulse for up to 30-45 seconds before starting CPR',
                'Compressions may be delivered at a slower rate if severe hypothermia is present',
                'Continue CPR until the victim is rewarmed'
              ]
            },
            {
              scenario: 'Opioid Overdose',
              adaptations: [
                'Standard CPR protocols apply',
                'If available, administer naloxone as soon as possible',
                'Continue CPR until the victim responds or advanced help arrives'
              ]
            }
          ]
        }
      },
      quiz: {
        title: 'CPR Formula Knowledge Check',
        description: 'Test your understanding of CPR ratios and timing',
        questions: [
          {
            id: 'cpr-formula-1',
            question: 'What is the correct compression-to-breath ratio for adult CPR with a single rescuer?',
            options: [
              '15:2',
              '30:2',
              '5:1',
              '100:1'
            ],
            correctAnswer: 1,
            explanation: 'For adult CPR with either one or two rescuers, the correct ratio is 30 compressions followed by 2 rescue breaths.'
          },
          {
            id: 'cpr-formula-2',
            question: 'What is the recommended compression rate for all age groups?',
            options: [
              '60-80 compressions per minute',
              '80-100 compressions per minute',
              '100-120 compressions per minute',
              '120-140 compressions per minute'
            ],
            correctAnswer: 2,
            explanation: 'The recommended compression rate for all age groups is 100-120 compressions per minute.'
          },
          {
            id: 'cpr-formula-3',
            question: 'How deep should chest compressions be for an adult victim?',
            options: [
              'At least 1 inch (2.5 cm)',
              'At least 1.5 inches (4 cm)',
              'At least 2 inches (5 cm)',
              'At least 3 inches (7.5 cm)'
            ],
            correctAnswer: 2,
            explanation: 'For adults, compress the chest at least 2 inches (5 cm) but not more than 2.4 inches (6 cm).'
          },
          {
            id: 'cpr-formula-4',
            question: 'When performing two-rescuer CPR on a child, what is the correct compression-to-breath ratio?',
            options: [
              '30:2',
              '15:2',
              '5:1',
              '20:2'
            ],
            correctAnswer: 1,
            explanation: 'For two-rescuer CPR on children and infants, the ratio changes to 15 compressions followed by 2 breaths.'
          }
        ]
      }
    }
  },
  {
    id: '4.6',
    title: 'Use of Pocket Masks and Bag Valve Mask Devices',
    description: 'Proper use of respiratory support devices',
    duration: 35,
    type: 'Lesson',
    content: {
      introduction: 'Pocket masks and bag valve mask (BVM) devices are essential tools for providing rescue breathing and ventilation during CPR. These devices help protect both the rescuer and the victim while ensuring effective ventilation. This comprehensive lesson covers proper techniques, step-by-step procedures, and best practices for using these devices in various emergency situations.',
      objectives: [
        'Understand when and how to use pocket masks and BVM devices',
        'Master proper positioning, sealing techniques, and ventilation procedures',
        'Learn to assemble and troubleshoot BVM devices',
        'Recognize and address common challenges during ventilation',
        'Apply safety precautions to prevent cross-contamination',
        'Understand the differences between adult, child, and infant ventilation techniques'
      ],
      mainContent: {
        // pocketMask: {
        //   title: 'Pocket Mask Usage',
        //   content: 'Pocket masks are portable, easy-to-use devices that provide a barrier between the rescuer and the victim during rescue breathing. They are essential for preventing disease transmission while delivering effective ventilations. Modern pocket masks typically include a one-way valve, oxygen inlet, and transparent mask for monitoring the victim\'s condition.',
        //   components: [
        //     'Mask: Clear, flexible plastic that covers the nose and mouth',
        //     'One-way valve: Prevents backflow of air and bodily fluids',
        //     'Oxygen inlet: For connecting supplemental oxygen (15 L/min recommended)',
        //     'Strap: For securing the mask to the victim\'s face when needed',
        //     'Carrying case: For protection and portability'
        //   ],
        //   steps: [
        //     'Position yourself at the victim\'s side and open the airway using the head-tilt, chin-lift maneuver (or jaw-thrust if spinal injury is suspected)',
        //     'Remove the mask from its case and place it over the victim\'s nose and mouth',
        //     'Ensure the narrower end of the mask is over the bridge of the nose',
        //     'Use both hands to create a tight seal: place your thumbs on the top of the mask and your fingers along the jawbone',
        //     'Deliver rescue breaths by blowing into the one-way valve',
        //     'Watch for chest rise with each breath (should take about 1 second per breath)',
        //     'Allow for complete exhalation between breaths (watch for chest to fall)',
        //     'If using supplemental oxygen, ensure proper flow rate (15 L/min)'
        //   ],
        //   tips: [
        //     'Always use the one-way valve to prevent contact with the victim\'s bodily fluids',
        //     'Maintain head tilt while keeping the mask sealed (use the E-C technique if needed)',
        //     'For single-rescuer CPR, use the pocket mask between compression cycles',
        //     'Practice with different sized masks to ensure proper fit for various face sizes',
        //     'Carry multiple sizes if providing care to both adults and children',
        //     'Replace masks that become stiff, cracked, or damaged'
        //   ],
        //   specialConsiderations: {
        //     pediatricUse: {
        //       title: 'Pediatric Considerations',
        //       points: [
        //         'Use appropriately sized pediatric masks',
        //         'Be gentle with head positioning to avoid hyperextension',
        //         'Smaller breaths are needed - watch for gentle chest rise',
        //         'Consider using a pediatric-specific pocket mask with smaller dead space'
        //       ]
        //     },
        //     specialSituations: {
        //       title: 'Special Situations',
        //       points: [
        //         'For bearded victims, use water-soluble lubricant to improve seal',
        //         'For trauma victims, use jaw-thrust instead of head-tilt if spinal injury is suspected',
        //         'In cold environments, watch for mask fogging which may indicate proper ventilation',
        //         'For victims with dentures, leave them in place to maintain facial structure'
        //       ]
        //     }
        //   }
        // },
        bvmDevice: {
          title: 'Bag Valve Mask (BVM) Usage',
          content: 'Bag Valve Masks (BVM) are the gold standard for providing positive-pressure ventilation in healthcare settings. They allow for higher oxygen concentrations and more controlled ventilation than pocket masks. Proper technique is essential for effective ventilation and to prevent complications such as gastric insufflation and barotrauma.',
          components: [
            'Mask: Clear, cushioned facepiece that covers the nose and mouth (available in various sizes)',
            'Bag: Self-inflating reservoir (adult: 1600ml, child: 450ml, infant: 240ml)',
            'Oxygen inlet: Standard 22mm connector for oxygen tubing',
            'One-way valve: Prevents rebreathing of exhaled air and directs oxygen flow',
            'Reservoir bag: For high-concentration oxygen delivery (minimum 1.5L capacity)',
            'Pop-off valve: Safety feature to prevent excessive pressure (may need to be disabled for some patients)',
            'PEEP valve: Optional attachment for positive end-expiratory pressure'
          ],
          setup: {
            title: 'Equipment Setup',
            steps: [
              'Connect the mask to the bag and ensure all connections are secure',
              'Attach oxygen tubing to the oxygen inlet and set flow to 15 L/min',
              'Allow the reservoir bag to fill completely (should not collapse more than 1/3 during ventilation)',
              'Test the system by squeezing the bag and ensuring proper valve function',
              'Select the appropriate mask size (adult, child, or infant)'
            ]
          },
          technique: {
            title: 'Ventilation Techniques',
            onePersonTechnique: {
              title: 'One-Person Technique',
              steps: [
                'Position yourself at the top of the victim\'s head',
                'Open the airway using head-tilt, chin-lift (or jaw-thrust if trauma is suspected)',
                'Hold the mask with your non-dominant hand using the E-C technique',
                'Squeeze the bag with your dominant hand to deliver breaths (1 second per breath)',
                'Watch for chest rise and fall with each breath',
                'Maintain proper head position throughout'
              ]
            },
            twoPersonTechnique: {
              title: 'Two-Person Technique (Preferred)',
              steps: [
                'Rescuer 1 (at the head): Positions the mask and maintains airway',
                'Rescuer 2 (at the side): Squeezes the bag to deliver breaths',
                'Use the E-C clamp technique for optimal mask seal',
                'Coordinate breaths with chest compressions during CPR',
                'Communicate clearly about ventilation quality and any issues'
              ]
            },
            ventilationParameters: {
              title: 'Ventilation Parameters',
              parameters: [
                'Rate: 10-12 breaths/min (1 breath every 5-6 seconds) for adults',
                'Tidal Volume: 400-600 mL (6-7 mL/kg ideal body weight)',
                'Inspiratory Time: 1 second per breath',
                'Peak Pressure: 20-30 cm H2O (watch for chest rise)',
                'FiO2: 85-100% with reservoir and 15 L/min O2 flow'
              ]
            }
          },
          considerations: [
            'Use supplemental oxygen when available (10-15 L/min)',
            'Squeeze the bag smoothly and evenly over 1 second',
            'Watch for chest rise with each ventilation',
            'Avoid over-ventilation (can cause gastric inflation and reduce cardiac output)',
            'Monitor oxygen saturation if available',
            'Be prepared to suction if necessary'
          ]
        },
        commonChallenges: {
          title: 'Common Challenges and Solutions',
          issues: [
            {
              problem: 'Difficulty maintaining seal',
              solution: 'Reposition the mask, adjust head position, or try a different size mask'
            },
            {
              problem: 'No chest rise',
              solution: 'Recheck head position, ensure proper mask seal, check for airway obstruction'
            },
            {
              problem: 'Gastric inflation',
              solution: 'Reduce ventilation volume and pressure, ensure proper head position'
            },
            {
              problem: 'Fogging of the mask',
              solution: 'Check for proper seal, ensure oxygen is connected and flowing'
            }
          ]
        },
        safetyPrecautions: {
          title: 'Safety Precautions',
          points: [
            'Use standard precautions (gloves, eye protection)',
            'Ensure proper cleaning and disinfection between uses',
            'Check equipment before use (no tears in mask, bag inflates properly)',
            'Monitor for complications (vomiting, pneumothorax)'
          ]
        }
      },
      keyPoints: [
        'Pocket masks are ideal for single rescuers, while BVM devices are preferred for healthcare providers and two-rescuer CPR',
        'The E-C clamp technique is the gold standard for maintaining a proper mask seal',
        'Ventilate at a rate of 1 breath every 5-6 seconds during CPR (10-12 breaths per minute)',
        'For BVM, use the two-person technique whenever possible for optimal ventilation',
        'Always watch for chest rise to confirm effective ventilation',
        'Use supplemental oxygen at 10-15 L/min when available',
        'Regularly check and maintain your equipment to ensure readiness',
        'Practice proper cleaning and disinfection procedures after each use'
      ],
      quiz: {
        title: 'Pocket Masks and BVM Knowledge Check',
        description: 'Test your understanding of pocket mask and BVM usage with these questions:',
        questions: [
          {
            id: '4.6.1',
            question: 'What is the primary advantage of using a pocket mask with a one-way valve?',
            options: [
              'It filters the air being delivered to the victim',
              'It prevents the rescuer from coming into contact with the victim\'s exhaled air',
              'It increases the oxygen concentration of the delivered air',
              'It eliminates the need for proper head tilt'
            ],
            correctAnswer: 1,
            explanation: 'The one-way valve in a pocket mask is designed to prevent the rescuer from coming into contact with the victim\'s exhaled air, reducing the risk of disease transmission.'
          },
          {
            id: '4.6.2',
            question: 'When using a BVM device, what is the recommended ventilation rate for an adult patient in cardiac arrest?',
            options: [
              '5-6 breaths per minute',
              '10-12 breaths per minute',
              '15-20 breaths per minute',
              '20-30 breaths per minute'
            ],
            correctAnswer: 1,
            explanation: 'The recommended ventilation rate for an adult in cardiac arrest is 10-12 breaths per minute (1 breath every 5-6 seconds).'
          },
          {
            id: '4.6.3',
            question: 'Which of the following best describes the E-C clamp technique used with a BVM?',
            options: [
              'Using your thumb and index finger to form a \'C\' around the mask while using your other fingers to lift the jaw',
              'Placing your entire hand over the mask and pressing down firmly',
              'Using two hands to form a complete seal around the mask',
              'Holding the mask with one hand and squeezing the bag with the other'
            ],
            correctAnswer: 0,
            explanation: 'The E-C clamp technique involves forming a \'C\' with your thumb and index finger around the mask while using your remaining fingers to lift the jaw (forming an \'E\' shape). This provides both a good mask seal and proper head position.'
          },
          {
            id: '4.6.4',
            question: 'What is the most common cause of gastric inflation during BVM ventilation?',
            options: [
              'Insufficient oxygen flow',
              'Ventilating too quickly',
              'Excessive ventilation volume or pressure',
              'Using room air instead of supplemental oxygen'
            ],
            correctAnswer: 2,
            explanation: 'Gastric inflation most commonly occurs due to excessive ventilation volume or pressure, which can force air into the esophagus and stomach rather than the lungs.'
          },
          {
            id: '4.6.5',
            question: 'When should you consider using a two-person BVM technique?',
            options: [
              'Only when the patient is not breathing',
              'When available, as it provides better ventilation than one-person technique',
              'Only for pediatric patients',
              'Only when supplemental oxygen is not available'
            ],
            correctAnswer: 1,
            explanation: 'The two-person BVM technique should be used whenever possible as it allows one rescuer to maintain a proper mask seal while the other squeezes the bag, resulting in better ventilation.'
          },
          {
            id: '4.6.6',
            question: 'What is the recommended oxygen flow rate when using a BVM with a reservoir?',
            options: [
              '5 L/min',
              '10-15 L/min',
              '20-25 L/min',
              'As high as the flowmeter will go'
            ],
            correctAnswer: 1,
            explanation: 'When using a BVM with a reservoir, the recommended oxygen flow rate is 10-15 L/min to ensure adequate oxygen delivery.'
          },
          {
            id: '4.6.7',
            question: 'What is the most important indicator of effective ventilation?',
            options: [
              'Hearing air escape during exhalation',
              'Feeling resistance when squeezing the bag',
              'Seeing the chest rise with each breath',
              'Counting to two seconds per breath'
            ],
            correctAnswer: 2,
            explanation: 'The most important indicator of effective ventilation is seeing the chest rise with each breath, which confirms that air is entering the lungs.'
          },
          {
            id: '4.6.8',
            question: 'How should you clean a pocket mask after use?',
            options: [
              'Wipe with an alcohol swab and let air dry',
              'Soak in a bleach solution for 10 minutes',
              'Follow manufacturer\'s instructions for cleaning and disinfection',
              'Rinse with water and let air dry'
            ],
            correctAnswer: 2,
            explanation: 'Always follow the manufacturer\'s instructions for cleaning and disinfection, as different materials may require specific cleaning procedures.'
          },
          {
            id: '4.6.9',
            question: 'What is the recommended volume for each breath when using a BVM on an adult patient?',
            options: [
              '200-300 mL',
              '400-600 mL',
              '700-900 mL',
              '1,000-1,200 mL'
            ],
            correctAnswer: 1,
            explanation: 'The recommended tidal volume for an adult patient is 400-600 mL (6-7 mL/kg of ideal body weight), delivered over 1 second.'
          },
          {
            id: '4.6.10',
            question: 'What should you do if you notice the victim\'s stomach rising instead of their chest during ventilation?',
            options: [
              'Increase the ventilation pressure',
              'Reposition the head and ensure proper mask seal',
              'Stop ventilations immediately',
              'Continue as long as some air is going in'
            ],
            correctAnswer: 1,
            explanation: 'If you notice gastric inflation (stomach rising), you should reposition the head to ensure the airway is open and check your mask seal. Gastric inflation can lead to vomiting and aspiration.'
          }
        ]
      }
    }
  },
  {
    id: '4.7',
    title: 'Recovery Position',
    description: 'Learn how to properly place an unconscious but breathing person in the recovery position',
    duration: 25,
    type: 'Interactive Lesson',
    content: {
      id: '4.7',
      introduction: 'The rescue position, also known as the recovery position, is a crucial technique used to maintain an open airway in unconscious patients who are breathing normally. This position helps prevent aspiration of fluids into the lungs and ensures that the airway remains clear. Knowing when and how to place someone in the rescue position can be life-saving in emergency situations.',
      objectives: [
        'Understand when to use the rescue position',
        'Learn the step-by-step procedure for placing someone in the rescue position',
        'Recognize when NOT to use the rescue position',
        'Understand special considerations for pregnant women and injured patients',
        'Practice the technique through guided exercises'
      ],
      mainContent: {
        
        recoveryPositionSteps: {
          title: 'Recovery Position Steps',
          steps: [
            {
              step: 1,
              title: 'Assess Responsiveness',
              description: 'Before moving the patient, assess their responsiveness. Tap and shout to check for any signs of consciousness or response.'
            },
            {
              step: 2,
              title: 'Ensure the Scene is Safe',
              description: 'Ensure the immediate environment is safe for both you and the patient, checking for hazards or dangers.'
            },
            {
              step: 3,
              title: 'Positioning',
              description: 'Kneel beside the patient. Extend the patient\'s arm nearest to you straight out from their body, perpendicular to their torso. Place the other arm across the patient\'s chest and hold the back of their hand against the cheek nearest to you.'
            },
            {
              step: 4,
              title: 'Leg Movement',
              description: 'With your other hand, grasp the leg furthest from you just above the knee and pull it up, keeping the foot on the ground.'
            },
            {
              step: 5,
              title: 'Roll Over',
              description: 'Keeping the hand pressed against the cheek, pull on the bent leg and roll the patient towards you onto their side. Gently roll the person onto their side, maintaining the position of their extended arm, bent knee, and bent elbow. This should allow the person to rest on their side with their head tilted slightly backward to keep the airway open. Ensure that the person\'s airway remains open and unobstructed.'
            },
            {
              step: 6,
              title: 'Final Adjustments',
              description: 'Adjust the upper leg so that the hip and knee are bent at right angles to keep the patient from rolling over onto their stomach.Tilt the head back slightly to ensure the airway remains open.Adjust the hand under the cheek as necessary to keep the head tilted.'
            },
            {
              step: 7,
              title: 'Monitoring',
              description: 'Check the patient\'s breathing and pulse regularly until emergency help arrives. If at any point the patient stops breathing, initiate CPR.'
            }
          ]
        },
       
      }
    }
  }
];

export const module5_lessons = [
  {
    id: '5.1',
    title: 'Introduction to AEDs - AED Basics and Components',
    description: 'Understanding Automated External Defibrillators',
    duration: 25,
    type: 'Introduction',
    content: {
      introduction: 'Automated External Defibrillators (AEDs) are life-saving devices designed to help people experiencing sudden cardiac arrest. This lesson will introduce you to AEDs, their importance in emergency response, and the basic principles of their operation.',
      objectives: [
        'Understand what an AED is and its purpose',
        'Learn when and why AEDs are used',
        'Recognize the importance of early defibrillation',
        'Understand basic AED safety and operation'
      ],
      mainContent: {
        whatIsAED: {
          title: 'What is an AED?',
          content: 'An Automated External Defibrillator (AED) is a portable electronic device that automatically diagnoses life-threatening cardiac arrhythmias and can treat them through defibrillation. Defibrillation is the application of electrical therapy which stops the arrhythmia, allowing the heart to re-establish an effective rhythm.',
          lessonImages: [
            {
              src: '/images/aed/1.png',
              alt: 'AED Device',
              caption: 'Standard AED device with pads and instructions'
            },
            {
              src: '/images/aed/2.png',
              alt: 'AED Placement',
              caption: 'Proper pad placement on patient\'s chest'
            }
          ],
          keyPoints: [
            'Portable and easy to use',
            'Designed for use by the general public',
            'Provides automatic heart rhythm analysis',
            'Gives clear voice and visual instructions'
          ]
        },
        importance: {
          title: 'Importance of AEDs',
          content: 'AEDs play a crucial role in the Chain of Survival for cardiac arrest victims. For every minute that passes without defibrillation, the chance of survival decreases by 7-10%. Having AEDs readily available in public places can significantly improve survival rates.',
          statistics: [
            {
              fact: 'Survival Rate with Early AED Use',
              description: 'When an AED is used within 3-5 minutes of collapse, survival rates can be as high as 50-70%.',
              icon: 'TrendingUp',
              color: '#2e7d32'
            },
            {
              fact: 'Response Time Impact',
              description: 'Every minute without defibrillation reduces survival chance by 7-10%.',
              icon: 'Timer',
              color: '#d32f2f'
            },
            {
              fact: 'Public Access Success',
              description: 'Public access to AEDs has shown to double the chance of survival from cardiac arrest.',
              icon: 'Public',
              color: '#1976d2'
            }
          ]
        },
        whenToUse: {
          title: 'When to Use an AED',
          description: 'AEDs should be used on people who are:',
          indicators: [
            {
              condition: 'Unconscious',
              description: 'The person is unresponsive when tapped and shouted at.',
              icon: 'PersonOff',
              color: '#d32f2f'
            },
            {
              condition: 'Not Breathing',
              description: 'The person shows no signs of normal breathing or only gasping.',
              icon: 'Air',
              color: '#f57c00'
            },
            {
              condition: 'No Signs of Circulation',
              description: 'No pulse or other signs of circulation are present.',
              icon: 'MonitorHeart',
              color: '#c2185b'
            }
          ]
        },
        basicOperation: {
          title: 'Basic AED Operation',
          steps: [
            {
              step: 1,
              title: 'Power On',
              description: 'Press the power button to turn on the AED. Some units will power on automatically when you open the lid.',
              icon: 'Power',
              color: '#1976d2'
            },
            {
              step: 2,
              title: 'Follow Voice Prompts',
              description: 'The AED will provide clear voice instructions. Listen carefully and follow each step.',
              icon: 'VolumeUp',
              color: '#388e3c'
            },
            {
              step: 3,
              title: 'Attach Pads',
              description: 'Place the pads on the patient\'s bare chest exactly as shown in the pad diagram.',
              icon: 'TouchApp',
              color: '#0097a7'
            },
            {
              step: 4,
              title: 'Clear the Patient',
              description: 'When instructed, ensure no one is touching the patient while the AED analyzes the heart rhythm.',
              icon: 'PanTool',
              color: '#e64a19'
            }
          ]
        },
        safetyConcerns: {
          title: 'Safety Considerations',
          description: 'Understanding these safety considerations is crucial for effective and safe AED use:',
          concerns: [
            {
              title: 'Environmental Conditions',
              description: 'Remove patient from water or metal surfaces. Dry chest if wet. Move away from flammable materials.',
              icon: 'WaterDrop',
              color: '#0288d1'
            },
            {
              title: 'Medical Devices',
              description: 'Check for and remove medication patches. Note presence of implanted devices.',
              icon: 'HealthAndSafety',
              color: '#7b1fa2'
            },
            {
              title: 'Chest Hair',
              description: 'Excessive chest hair may need to be quickly removed for proper pad adhesion.',
              icon: 'ContentCut',
              color: '#455a64'
            },
            {
              title: 'Jewelry and Metal',
              description: 'Remove metal jewelry or accessories near pad placement areas.',
              icon: 'Watch',
              color: '#bf360c'
            }
          ]
        },
        specialCircumstances: {
          title: 'Special Circumstances',
          content: 'AEDs can be used in various special circumstances with proper considerations:',
          circumstances: [
            {
              situation: 'Pediatric Patients',
              guidelines: [
                'Use pediatric pads for children under 8 years or 25kg',
                'If no pediatric pads available, adult pads can be used',
                'Place pads in anterior-posterior position for small children'
              ],
              icon: 'ChildCare',
              color: '#00acc1'
            },
            {
              situation: 'Pregnant Patients',
              guidelines: [
                'AEDs are safe to use on pregnant women',
                'Standard pad placement should be used',
                'Immediate defibrillation if needed'
              ],
              icon: 'PregnantWoman',
              color: '#ec407a'
            },
            {
              situation: 'Implanted Devices',
              guidelines: [
                'Place pads away from visible implanted devices',
                'Maintain at least 1-inch distance from device',
                'Follow standard AED protocol'
              ],
              icon: 'DeviceHub',
              color: '#5c6bc0'
            }
          ]
        },
        maintenance: {
          title: 'AED Maintenance',
          description: 'Regular maintenance is essential for AED readiness:',
          checkList: [
            {
              task: 'Battery Check',
              frequency: 'Monthly',
              description: 'Verify battery indicator shows adequate power level.',
              icon: 'Battery',
              color: '#43a047'
            },
            {
              task: 'Pad Inspection',
              frequency: 'Monthly',
              description: 'Check pad expiration dates and packaging integrity.',
              icon: 'Assignment',
              color: '#fb8c00'
            },
            {
              task: 'Unit Self-Test',
              frequency: 'According to manufacturer',
              description: 'Confirm unit performs automatic self-tests and displays ready status.',
              icon: 'BuildCircle',
              color: '#6d4c41'
            },
            {
              task: 'Environmental Check',
              frequency: 'Weekly',
              description: 'Ensure AED is visible, accessible, and protected from extreme conditions.',
              icon: 'Room',
              color: '#546e7a'
            }
          ]
        }
      },
      quiz: {
        title: 'Knowledge Check',
        description: 'Test your understanding of AED basics',
        questions: [
          {
            id: 1,
            question: 'What does AED stand for?',
            options: [
              'Automatic Emergency Device',
              'Automated External Defibrillator',
              'Advanced Emergency Defibrillator',
              'Automatic External Device'
            ],
            correctAnswer: 1,
            explanation: 'AED stands for Automated External Defibrillator, a device that can analyze heart rhythms and deliver electrical shocks when necessary.'
          },
          {
            id: 2,
            question: 'How quickly should an AED be applied to a cardiac arrest victim?',
            options: [
              'Within 30 minutes',
              'Within 15 minutes',
              'As soon as possible, ideally within 3-5 minutes',
              'Only after CPR has been performed for 10 minutes'
            ],
            correctAnswer: 2,
            explanation: 'AEDs should be applied as soon as possible, ideally within 3-5 minutes, as the chance of survival decreases by 7-10% for every minute without defibrillation.'
          },
          {
            id: 3,
            question: 'When should you use an AED?',
            options: [
              'On anyone who is feeling unwell',
              'Only on people over 18 years old',
              'On someone who is unconscious but breathing normally',
              'On someone who is unconscious and not breathing normally'
            ],
            correctAnswer: 3,
            explanation: 'AEDs should be used on people who are unconscious and not breathing normally, as these are signs of cardiac arrest.'
          }
        ]
      }
    }
  },
  {
    id: '5.2',
    title: 'Components of AED',
    description: 'Detailed overview of AED parts and functions',
    duration: 30,
    type: 'Interactive Lesson',
    content: {
      introduction: 'Understanding the components of an Automated External Defibrillator (AED) is crucial for effective use during emergencies. This lesson will explore each essential part of an AED system and its specific function.',
      objectives: [
        'Identify and locate key components of an AED',
        'Understand the function and importance of each component',
        'Learn proper handling and maintenance requirements',
        'Recognize common variations in different AED models'
      ],
      mainContent: {
        overview: {
          title: 'Overview of AED Components',
          // content: 'An AED consists of several critical components working together to deliver life-saving therapy. Understanding each component helps ensure proper use and maintenance.',
          keyComponents: [
            {
              name: 'Main Unit',
              description: 'The central processing unit that controls all AED functions.',
              icon: 'Memory',
              color: '#1976d2'
            },
            {
              name: 'Display Screen/Indicators',
              description: 'Visual interface showing device status and instructions.',
              icon: 'Visibility',
              color: '#00acc1'
            },
            {
              name: 'Speaker System',
              description: 'Provides voice prompts and instructions during use.',
              icon: 'VolumeUp',
              color: '#43a047'
            }
          ]
        },
        essentialParts: {
          title: 'Essential Components',
          description: 'These components are crucial for AED operation:',
          parts: [
            {
              name: 'Electrode Pads',
              details: {
                description: 'Adhesive pads that deliver the defibrillation shock',
                features: [
                  'Pre-connected for quick application',
                  'Self-adhesive for secure placement',
                  'Conducts electrical current to the heart'
                ],
                maintenance: 'Check expiration date monthly',
                icon: 'TouchApp',
                color: '#e91e63'
              }
            },
            {
              name: 'Battery System',
              details: {
                description: 'Powers the AED and enables shock delivery',
                features: [
                  'Long-term battery life',
                  'Status indicator included',
                  'Easy replacement design'
                ],
                maintenance: 'Regular battery checks required',
                icon: 'Battery',
                color: '#ffc107'
              }
            },
            {
              name: 'Control Panel',
              details: {
                description: 'Interface for operating the AED',
                features: [
                  'Clear button layout',
                  'Simple user interface',
                  'Status indicators'
                ],
                maintenance: 'Keep clean and damage-free',
                icon: 'Dashboard',
                color: '#9c27b0'
              }
            }
          ]
        },
        additionalItems: {
          title: 'Additional Components',
          description: 'Supplementary items included with AED:',
          items: [
            {
              name: 'Carrying Case',
              description: 'Protects AED and holds accessories',
              icon: 'Work',
              color: '#795548'
            },
            {
              name: 'Emergency Kit',
              description: 'Contains scissors, razors, gloves, etc.',
              icon: 'MedicalServices',
              color: '#f44336'
            },
            {
              name: 'Quick Reference Card',
              description: 'Step-by-step visual instructions',
              icon: 'Description',
              color: '#607d8b'
            }
          ]
        }
      },
      quiz: {
        title: 'Component Recognition Quiz',
        description: 'Test your knowledge of AED components',
        questions: [
          {
            id: 1,
            question: 'Which component delivers the electrical shock to the patient?',
            options: [
              'The carrying case',
              'The electrode pads',
              'The control panel',
              'The battery'
            ],
            correctAnswer: 1,
            explanation: 'Electrode pads are the components that deliver the electrical shock to the patient\'s chest.'
          },
          {
            id: 2,
            question: 'What is the purpose of the AED\'s speaker system?',
            options: [
              'To play music during use',
              'To provide voice prompts and instructions',
              'To detect heart rhythms',
              'To charge the battery'
            ],
            correctAnswer: 1,
            explanation: 'The speaker system provides voice prompts and instructions to guide the user through the rescue process.'
          },
          {
            id: 3,
            question: 'Which item should be checked monthly for expiration?',
            options: [
              'The carrying case',
              'The quick reference card',
              'The electrode pads',
              'The control panel'
            ],
            correctAnswer: 2,
            explanation: 'Electrode pads should be checked monthly for expiration to ensure they will work effectively when needed.'
          }
        ]
      }
    }
  },
  {
    id: '5.3',
    title: 'Proper AED Placement and Coordination of CPR',
    description: 'Correct AED pad placement and CPR coordination',
    duration: 35,
    type: 'Lesson',
    content: {
      introduction: 'The use of an Automated External Defibrillator (AED) is a critical component in the Chain of Survival for someone experiencing sudden cardiac arrest. Understanding the proper placement and operation of an AED can significantly increase the chances of survival for the victim.',
      objectives: [
        'Learn proper AED pad placement techniques',
        'Understand the step-by-step operation process',
        'Master coordination between AED use and CPR',
        'Practice safety protocols during AED operation'
      ],
      mainContent: {
        preparation: {
          title: 'Initial Steps and Preparation',
          steps: [
            {
              title: 'Safety Check',
              description: 'Ensure the scene is safe before approaching the patient. Confirm the patient is unresponsive, not breathing, or only gasping.',
              icon: 'HealthAndSafety',
              color: '#2196f3'
            },
            {
              title: 'Call for Help',
              description: 'Activate the emergency response system or instruct someone else to do so. Ensure someone retrieves the AED.',
              icon: 'Call',
              color: '#f44336'
            }
          ]
        },
        chestPreparation: {
          title: 'Prepare the Chest Area',
          description: 'Before applying AED pads, ensure proper chest preparation:',
          steps: [
            {
              step: 'Ensure the chest is dry',
              details: 'If the chest is wet, wipe it dry to ensure proper adhesion of the AED pads and effective delivery of the shock.',
              icon: 'WaterDrop',
              color: '#00bcd4'
            },
            {
              step: 'Remove clothing',
              details: 'Remove any clothing or undergarments covering the chest area to expose the skin. Use scissors if necessary.',
              icon: 'ContentCut',
              color: '#9c27b0'
            },
            {
              step: 'Shave if needed',
              details: 'If the chest is excessively hairy and prevents pad adhesion, quickly shave the area where the pads will be placed.',
              icon: 'ContentCut',
              color: '#ff9800'
            }
          ]
        },
        padPlacement: {
          title: 'AED Pad Placement',
          description: 'Proper pad placement is crucial for effective defibrillation:',
          // Image removed as requested
          
          positions: [
            {
              pad: 'Right Pad',
              location: 'Place one AED pad on the upper right side of the chest, just below the collarbone.',
              icon: 'TouchApp',
              color: '#4caf50'
            },
            {
              pad: 'Left Pad',
              location: 'Place the other AED pad on the lower left side of the chest, a few inches below the armpit. This positioning helps to create a clear pathway through the heart.',
              icon: 'TouchApp',
              color: '#4caf50'
            }
          ]
        },
        operationSteps: {
          title: 'Proper AED Operation',
          steps: [
            {
              step: 1,
              action: 'Turn on the AED',
              description: 'Open the AED case and turn on the device. Most AEDs have a power button or automatically turn on when the lid is opened.',
              icon: 'Power',
              color: '#1976d2'
            },
            {
              step: 2,
              action: 'Follow the AED Prompts',
              description: 'The AED will provide voice and visual instructions. Follow these prompts carefully.',
              icon: 'VolumeUp',
              color: '#388e3c'
            },
            {
              step: 3,
              action: 'Attach the Pads',
              description: 'Connect the AED pads to the device if they are not pre-connected. Ensure the pads adhere firmly to the skin and follow the correct placement guidelines.',
              icon: 'TouchApp',
              color: '#e91e63'
            },
            {
              step: 4,
              action: 'Analyse the Heart Rhythm',
              description: 'Allow the AED to analyse the heart rhythm. Ensure no one is touching the patient during this time to avoid interference with the analysis.',
              icon: 'MonitorHeart',
              color: '#9c27b0'
            },
            {
              step: 5,
              action: 'Voice Prompt',
              description: 'The AED will usually instruct, "Stand clear" or "Do not touch the patient."',
              icon: 'VolumeUp',
              color: '#ff5722'
            },
            {
              step: 6,
              action: 'Deliver the Shock',
              description: 'If the AED determines that a shock is needed, it will prompt you to deliver the shock. Ensure everyone is clear of the patient before pressing the shock button.',
              icon: 'ElectricBolt',
              color: '#ffc107'
            }
          ]
        },
        continuedCare: {
          title: 'Continued Care and Documentation',
          items: [
            {
              title: 'Continue CPR Cycles',
              description: '2 minutes of chest compressions and AED analysis until help arrives, signs of life appear, or AED advises otherwise.',
              icon: 'Refresh',
              color: '#2196f3'
            },
            {
              title: 'Advanced Care Handover',
              description: 'Provide detailed handover including number of shocks delivered and duration of CPR performed.',
              icon: 'Assignment',
              color: '#4caf50'
            },
            {
              title: 'Documentation',
              description: 'Document the entire resuscitation effort, including time of collapse, actions taken, AED analysis, shocks delivered, and any changes in condition.',
              icon: 'Description',
              color: '#ff9800'
            }
          ]
        }
      },
      quiz: {
        title: 'AED Operation Knowledge Check',
        questions: [
          {
            id: 1,
            question: 'Where should the right AED pad be placed?',
            options: [
              'On the lower left chest',
              'Right below the right collarbone',
              'On the back',
              'On the abdomen'
            ],
            correctAnswer: 1,
            explanation: 'The right AED pad should be placed just below the right collarbone for proper current flow through the heart.'
          },
          {
            id: 2,
            question: 'What should you do immediately after delivering a shock?',
            options: [
              'Wait for the next analysis',
              'Check for a pulse',
              'Resume CPR starting with chest compressions',
              'Remove the AED pads'
            ],
            correctAnswer: 2,
            explanation: 'Immediately resume CPR starting with chest compressions after delivering a shock to maintain blood circulation.'
          },
          {
            id: 3,
            question: 'How long should you continue CPR between AED analyses?',
            options: [
              '30 seconds',
              '1 minute',
              '2 minutes',
              '5 minutes'
            ],
            correctAnswer: 2,
            explanation: 'Continue CPR for about 2 minutes between AED analyses, unless the patient shows signs of life or the AED provides different instructions.'
          }
        ]
      }
    }
  },
  {
    id: '5.4',
    title: 'Safety in Resuscitation During Basic Life Support',
    description: 'Essential safety measures and protocols for effective resuscitation',
    duration: 30,
    type: 'Lesson',
    content: {
      introduction: 'Safety during resuscitation in BLS is critical to protect both the rescuer and the patient. By adhering to personal protective measures, ensuring effective and safe CPR techniques, properly using and managing equipment, and following infection control protocols, construction workers and other professionals can perform resuscitation effectively and safely.',
      objectives: [
        'Learn essential safety protocols before approaching a patient',
        'Understand proper PPE usage and infection control measures',
        'Master safe techniques for airway management and chest compressions',
        'Practice proper equipment handling and coordination'
      ],
      mainContent: {
        sceneAssessment: {
          title: 'Scene Safety Assessment',
          description: 'Before initiating resuscitation, ensure the environment is safe:',
          items: [
            {
              title: 'Hazard Assessment',
              description: 'Look for potential hazards such as moving machinery, electrical wires, unstable structures, or hazardous materials.',
              icon: 'Warning',
              color: '#f44336'
            },
            {
              title: 'Personal Protection',
              description: 'Use correct PPE including disposable gloves, face mask, safety goggles or a face shield depending on the situation.',
              icon: 'HealthAndSafety',
              color: '#2196f3'
            }
          ]
        },
        techniqueSafety: {
          title: 'Safe Resuscitation Techniques',
          description: 'Follow these guidelines to ensure safe and effective resuscitation:',
          techniques: [
            {
              title: 'Airway Management',
              description: 'When using the head-tilt, chin-lift manoeuvre, avoid hyperextending the neck, especially if cervical spine injury is suspected. Use the jaw-thrust manoeuvre if spinal injury is suspected.',
              icon: 'AccessibilityNew',
              color: '#4caf50',
              emphasis: 'Minimize neck movement'
            },
            {
              title: 'Chest Compressions',
              description: 'Perform at a depth of at least 5 cm (2 inches) but not more than 6 cm (2.4 inches) and at a rate of 100-120 compressions per minute.',
              icon: 'Favorite',
              color: '#e91e63',
              emphasis: 'Allow chest to fully recoil between compressions'
            },
            {
              title: 'Minimize Interruptions',
              description: 'Only pause chest compressions for AED analysis and shocks, or when absolutely necessary.',
              icon: 'Timer',
              color: '#ff9800'
            }
          ]
        },
        equipmentSafety: {
          title: 'Equipment Safety',
          items: [
            {
              title: 'AED Pad Placement',
              description: 'Ensure AED pads are placed correctly on the patient\'s chest for effective defibrillation.',
              icon: 'PowerSettingsNew',
              color: '#9c27b0'
            },
            {
              title: 'Patient Clearance',
              description: 'Verbally and visually ensure that no one is touching the patient during AED analysis and shock delivery.',
              icon: 'PanTool',
              color: '#f44336'
            },
            {
              title: 'BVM Usage',
              description: 'For Bag-Valve-Mask (BVM), ensure a tight seal around the patient\'s mouth and nose. Deliver ventilations that make the chest rise visibly, avoiding excessive ventilation.',
              icon: 'Air',
              color: '#00bcd4'
            }
          ]
        },
        infectionControl: {
          title: 'Infection Control Measures',
          description: 'Follow proper hygiene protocols:',
          measures: [
            {
              title: 'Hand Hygiene',
              description: 'Wash hands with soap and water for at least 20 seconds before and after resuscitation. If unavailable, use alcohol-based hand sanitizer.',
              icon: 'WaterDrop',
              color: '#2196f3'
            }
          ]
        },
        additionalResources: {
          title: 'Additional Safety Guidelines',
          resources: [
            {
              title: 'Healthcare Settings Guidelines',
              description: 'Guidance for safer handling during cardiopulmonary resuscitation in healthcare settings (Resus Council UK)',
              link: 'Click Here',
              icon: 'LocalHospital',
              color: '#4caf50'
            },
            {
              title: 'COVID-19 Safety',
              description: 'How to ensure safe and effective resuscitation for patients with Covid-19',
              link: 'Click Here',
              icon: 'Coronavirus',
              color: '#ff5722'
            }
          ]
        }
      },
      quiz: {
        title: 'Safety Protocols Knowledge Check',
        questions: [
          {
            id: 1,
            question: 'What is the first step before approaching a patient for resuscitation?',
            options: [
              'Start chest compressions immediately',
              'Call for help',
              'Ensure the scene is safe and check for hazards',
              'Put on gloves'
            ],
            correctAnswer: 2,
            explanation: 'Scene safety assessment is crucial to protect both the rescuer and the patient from potential hazards.'
          },
          {
            id: 2,
            question: 'What is the correct depth for chest compressions?',
            options: [
              '3-4 cm',
              'At least 5 cm but not more than 6 cm',
              '7-8 cm',
              'As deep as possible'
            ],
            correctAnswer: 1,
            explanation: 'Chest compressions should be at least 5 cm (2 inches) but not more than 6 cm (2.4 inches) deep for effective circulation.'
          },
          {
            id: 3,
            question: 'When using a BVM, what is important to ensure?',
            options: [
              'Maximum ventilation force',
              'Rapid ventilations',
              'A tight seal and visible chest rise',
              'Continuous ventilation'
            ],
            correctAnswer: 2,
            explanation: 'Ensure a tight seal around the patient\'s mouth and nose, and deliver ventilations that make the chest rise visibly without excessive force.'
          }
        ]
      }
    }
  }
];

export const module6_lessons = [
  {
    id: '6.1',
    title: 'Post-Resuscitation Care and Monitoring',
    description: 'Essential steps and procedures for post-ROSC care',
    duration: 30,
    type: 'Lesson',
    content: {
      introduction: 'Post-resuscitation care is a critical phase that begins immediately after achieving Return of Spontaneous Circulation (ROSC). This lesson covers the essential aspects of monitoring and caring for a patient during this crucial period.',
      objectives: [
        'Understand the key components of post-resuscitation care',
        'Learn proper patient monitoring techniques',
        'Master assessment and stabilization procedures',
        'Understand documentation and communication requirements'
      ],
      mainContent: {
        keyAspects: {
          title: 'Key Aspects of Post-Resuscitation Care',
          description: 'The following components are essential for effective post-resuscitation care and monitoring:',
          aspects: [
            {
              title: 'Assessment and Stabilization',
              description: 'After achieving ROSC, assess the patient\'s vital signs, level of consciousness, and overall condition. This initial assessment is crucial for determining the patient\'s stability.',
              icon: 'HealthAndSafety',
              color: '#2196f3',
              details: [
                'Check vital signs (pulse, blood pressure, temperature)',
                'Assess level of consciousness',
                'Monitor overall condition',
                'Look for signs of improvement or deterioration'
              ]
            },
            {
              title: 'Airway Management',
              description: 'Ensure that the patient\'s airway remains open and clear. This is a continuous priority throughout post-resuscitation care.',
              icon: 'Air',
              color: '#4caf50',
              details: [
                'Maintain airway patency',
                'Clear any obstructions',
                'Position patient appropriately',
                'Consider recovery position if appropriate'
              ]
            },
            {
              title: 'Monitor Breathing and Circulation',
              description: 'Continuously monitor the patient\'s breathing, pulse, and oxygen saturation. Provide supplemental oxygen if available and monitor response.',
              icon: 'MonitorHeart',
              color: '#f44336',
              details: [
                'Check breathing rate and quality',
                'Monitor pulse strength and regularity',
                'Measure oxygen saturation if possible',
                'Administer supplemental oxygen when available'
              ]
            },
            {
              title: 'Level of Consciousness',
              description: 'Perform a rapid assessment of the patient\'s level of consciousness using the AVPU scale (Alert, Voice, Pain, Unresponsive). Monitor for any changes.',
              icon: 'Psychology',
              color: '#9c27b0',
              details: [
                'Use AVPU scale for assessment',
                'Check response to verbal stimuli',
                'Assess pain response if needed',
                'Note any changes in consciousness level'
              ]
            },
            {
              title: 'Position the Patient',
              description: 'If there is no suspicion of a spinal injury and the patient is breathing, place them in the recovery position to maintain airway patency.',
              icon: 'AccessibilityNew',
              color: '#ff9800',
              details: [
                'Check for spinal injury indicators',
                'Use recovery position when appropriate',
                'Ensure airway remains clear',
                'Monitor breathing in position'
              ]
            },
            {
              title: 'Continual Assessment',
              description: 'Continuously reassess the patient\'s vital signs, looking for signs of improvement or deterioration. Be prepared to respond to any changes.',
              icon: 'Assessment',
              color: '#2196f3',
              details: [
                'Regular vital sign checks',
                'Monitor for deterioration',
                'Document all changes',
                'Be prepared for CPR if needed'
              ]
            },
            {
              title: 'Family Communication',
              description: 'Communicate with the person\'s family or next of kin to provide updates on the patient\'s condition and prognosis while maintaining professionalism.',
              icon: 'Group',
              color: '#9c27b0',
              details: [
                'Provide regular updates',
                'Explain current status',
                'Answer questions clearly',
                'Maintain professional demeanor'
              ]
            }
          ]
        },
        patientCare: {
          title: 'Patient Care and Monitoring',
          description: 'Key actions to support and monitor the patient after initial help:',
          steps: [
            {
              title: 'Position the Patient',
              description: 'Place the patient in the recovery position if they are breathing and there’s no spinal injury suspected. This helps keep their airway clear and prevents choking.',
              icon: 'AccessibilityNew',
              color: '#ff9800'
            },
            {
              title: 'Continual Assessment',
              description: 'Regularly check the patient’s breathing, pulse, and responsiveness to track any changes in their condition and act quickly if things get worse.',
              icon: 'Assessment',
              color: '#2196f3'
            },
            {
              title: 'Be Prepared',
              description: 'If the patient stops breathing or becomes unresponsive again, be ready to restart CPR immediately and continue until help arrives.',
              icon: 'Warning',
              color: '#f44336'
            }
          ]
        }
,        
        communicationAndHandover: {
          title: 'Communication and Documentation',
          description: 'Important aspects of patient care handover:',
          items: [
            {
              title: 'Provide Reassurance',
              description: 'Stay with the patient, offering reassurance and comfort while waiting for advanced medical help to arrive.',
              icon: 'SupportAgent',
              color: '#4caf50'
            },
            {
              title: 'Transfer of Care',
              description: 'When emergency medical services arrive, provide a handover that includes details of the incident, CPR duration, and the number of defibrillation shocks given.',
              icon: 'Transform',
              color: '#ff9800'
            },
            {
              title: 'Documentation',
              description: 'Document all interventions, observations, and persons responses as soon as possible, as per legal and professional requirements.',
              icon: 'Description',
              color: '#2196f3'
            },
            {
              title: 'Family Communication',
              description: 'Communicate with the person\'s family or next of kin to provide updates on the patient\'s condition and prognosis.',
              icon: 'Group',
              color: '#9c27b0'
            }
          ]
        }
      },
      quiz: {
        title: 'Post-Resuscitation Care Assessment',
        description: 'Test your understanding of post-resuscitation care procedures',
        questions: [
          {
            id: 'post-resuscitation-1',
            question: 'What should be assessed immediately after achieving ROSC?',
            options: [
              'Only the patient\'s breathing',
              'Only the pulse rate',
              'Vital signs, level of consciousness, and overall condition',
              'Just the temperature'
            ],
            correctAnswer: 2,
            explanation: 'After ROSC, it\'s crucial to assess the patient\'s vital signs, level of consciousness, and overall condition for comprehensive evaluation.'
          },
          {
            id: 'post-resuscitation-2',
            question: 'What scale is used to assess the patient\'s level of consciousness?',
            options: [
              'FAST scale',
              'AVPU scale',
              'Glasgow scale',
              'RICE scale'
            ],
            correctAnswer: 1,
            explanation: 'The AVPU scale (Alert, Voice, Pain, Unresponsive) is used for rapid assessment of consciousness level.'
          },
          {
            id: 'post-resuscitation-3',
            question: 'When should you be prepared to restart CPR?',
            options: [
              'Only when emergency services arrive',
              'After 30 minutes',
              'If the patient becomes unresponsive or breathing becomes absent/abnormal',
              'Only if family requests'
            ],
            correctAnswer: 2,
            explanation: 'Be prepared to restart CPR if the patient becomes unresponsive or their breathing becomes absent or abnormal.'
          }
        ]
      }
    }
  },

  {
    id: '6.2',
    title: 'Documentation and Reporting',
    description: 'Essential documentation and reporting procedures in post-resuscitation care',
    duration: 30,
    type: 'Lesson',
    content: {
      introduction: 'Documentation and reporting are critical components of post-resuscitation care. Proper documentation ensures continuity of care, legal compliance, and effective communication between healthcare providers. This lesson outlines the key aspects of post-resuscitation documentation and reporting procedures.',
      objectives: [
        'Understand the importance of accurate and timely documentation',
        'Learn essential elements to document during post-resuscitation care',
        'Master proper communication and handover procedures',
        'Understand legal and professional documentation requirements'
      ],
      mainContent: {
        keyAspects: {
          title: 'The Key Aspects of Post-Resuscitation Care and Monitoring',
          description: 'Essential elements that must be documented and monitored:',
          aspects: [
            {
              title: 'Assessment and Stabilization',
              description: 'After achieving ROSC, assess the patient\'s vital signs, level of consciousness, and overall condition.',
              icon: 'HealthAndSafety',
              color: '#2196f3',
              details: [
                'Record vital signs',
                'Document level of consciousness',
                'Note overall patient condition',
                'Track any changes in status'
              ]
            },
            {
              title: 'Airway Management',
              description: 'Ensure that the patient\'s airway remains open.',
              icon: 'Air',
              color: '#4caf50',
              details: [
                'Document airway status',
                'Record any interventions',
                'Note breathing quality',
                'Monitor for changes'
              ]
            },
            {
              title: 'Monitor Breathing and Circulation',
              description: 'Continuously monitor the patient\'s breathing, pulse, and oxygen saturation. Provide supplemental oxygen if available.',
              icon: 'MonitorHeart',
              color: '#f44336',
              details: [
                'Record breathing rate and quality',
                'Document pulse characteristics',
                'Note oxygen saturation levels',
                'Track any interventions provided'
              ]
            },
            {
              title: 'Level of Consciousness',
              description: 'Perform a rapid assessment of the patient\'s level of consciousness using the AVPU scale (Alert, Voice, Pain, Unresponsive).',
              icon: 'Psychology',
              color: '#9c27b0',
              details: [
                'Document AVPU scale assessment',
                'Note response to stimuli',
                'Track consciousness changes',
                'Record neurological signs'
              ]
            }
          ]
        },
        patientPositioning: {
          title: 'Patient Positioning and Monitoring',
          description: 'Document the following aspects of patient positioning and continuous monitoring:',
          items: [
            {
              title: 'Position the Patient',
              description: 'If there is no suspicion of a spinal injury and the patient is breathing, place them in the recovery position.',
              icon: 'AccessibilityNew',
              color: '#ff9800'
            },
            {
              title: 'Continual Assessment',
              description: 'Continuously reassess the patient\'s vital signs, looking for signs of improvement or deterioration.',
              icon: 'Assessment',
              color: '#2196f3'
            },
            {
              title: 'CPR Readiness',
              description: 'Be prepared to initiate CPR again if the patient\'s condition worsens and they become unresponsive or their breathing becomes absent or abnormal.',
              icon: 'Warning',
              color: '#f44336'
            }
          ]
        },
        communicationAndHandover: {
          title: 'Communication and Documentation',
          description: 'Important aspects of communication and documentation:',
          items: [
            {
              title: 'Provide Reassurance',
              description: 'Stay with the patient, offering reassurance and comfort while waiting for advanced medical help to arrive.',
              icon: 'SupportAgent',
              color: '#4caf50'
            },
            {
              title: 'Transfer of Care',
              description: 'When emergency medical services arrive, provide a handover that includes details of the incident, CPR duration, and the number of defibrillation shocks given.',
              icon: 'Transform',
              color: '#ff9800'
            },
            {
              title: 'Documentation',
              description: 'Document all interventions, observations, and persons responses as soon as possible, as per legal and professional requirements.',
              icon: 'Description',
              color: '#2196f3'
            },
            {
              title: 'Family Communication',
              description: 'Communicate with the person\'s family or next of kin to provide updates on the patient\'s condition and prognosis.',
              icon: 'Group',
              color: '#9c27b0'
            }
          ]
        },
        professionalGuidelines: {
          title: 'Professional Guidelines',
          description: 'Important professional considerations:',
          content: 'It is important for healthcare workers to receive regular, accredited training in post-resuscitation care and to be familiar with the specific protocols and resources available in their workplace. These guidelines are in line with the Resuscitation Council (UK) and should be adapted to comply with any updated recommendations or local policies.',
          icon: 'Assignment',
          color: '#2196f3'
        }
      },
      quiz: {
        title: 'Documentation and Reporting Assessment',
        description: 'Test your understanding of post-resuscitation documentation',
        questions: [
          {
            id: 'documentation-1',
            question: 'What should be documented during the initial assessment?',
            options: [
              'Only the patient\'s name',
              'Only vital signs',
              'Vital signs, level of consciousness, and overall condition',
              'Just the time of arrival'
            ],
            correctAnswer: 2,
            explanation: 'The initial assessment documentation should include vital signs, level of consciousness, and the patient\'s overall condition for comprehensive care.'
          },
          {
            id: 'documentation-2',
            question: 'When should you be prepared to restart CPR?',
            options: [
              'Only when EMS arrives',
              'If the patient becomes unresponsive or breathing becomes absent/abnormal',
              'After completing documentation',
              'Only during handover'
            ],
            correctAnswer: 1,
            explanation: 'You should be prepared to restart CPR if the patient becomes unresponsive or their breathing becomes absent or abnormal.'
          },
          {
            id: 'documentation-3',
            question: 'What information should be included in the EMS handover?',
            options: [
              'Only patient name',
              'Only time of incident',
              'Incident details, CPR duration, and number of defibrillation shocks',
              'Just family contact information'
            ],
            correctAnswer: 2,
            explanation: 'The handover should include comprehensive information about the incident, including CPR duration and the number of defibrillation shocks given.'
          }
        ]
      }
    }
  }
];

export const module7_lessons = [
  {
    id: '7.1',
    title: 'Choking',
    description: 'Understanding and managing choking emergencies',
    duration: 15,
    type: 'Introduction',
    content: {
      introduction: 'Choking occurs when a foreign object obstructs the airway, preventing air from reaching the lungs. It\'s a serious and potentially life-threatening situation that requires immediate intervention.',
      objectives: [
        'Understand common causes of choking',
        'Recognize signs and symptoms of choking',
        'Differentiate between mild and severe airway obstruction'
      ],
      mainContent: {
        causesOfChoking: {
          title: 'Causes of Choking',
          description: 'Understanding the common causes can help prevent choking incidents:',
          causes: [
            {
              title: 'Food Obstruction',
              description: 'The most common cause of choking is food getting lodged in the throat, especially in individuals who eat too quickly, don\'t chew food thoroughly, or have difficulty swallowing.'
            },
            {
              title: 'Foreign Objects',
              description: 'Small objects, toys, or parts of toys can be choking hazards, particularly for young children who often put things in their mouths.'
            },
            {
              title: 'Medical Conditions',
              description: 'Conditions such as certain neurological disorders or dysphagia (difficulty swallowing) can increase the risk of choking.'
            },
            {
              title: 'Alcohol Intoxication',
              description: 'Alcohol can impair motor control and the gag reflex, increasing the risk of choking.'
            }
          ],
          commonRiskFactors: [
            'Talking excitedly or laughing while eating',
            'Eating too fast',
            'Wearing dentures',
            'Walking, playing or running with food or objects in the mouth',
            'Drinking alcohol before or during meals',
            'Trying to swallow large pieces of poorly chewed food'
          ]
        },
        symptomsAndSigns: {
          title: 'Symptoms of Choking',
          description: 'When someone is choking, they might exhibit the following signs:',
          progressiveSigns: [
            {
              order: 1,
              sign: 'Coughing or gagging',
              severity: 'Initial response'
            },
            {
              order: 2,
              sign: 'Inability to speak',
              severity: 'Moderate'
            },
            {
              order: 3,
              sign: 'Inability to breathe',
              severity: 'Severe'
            },
            {
              order: 4,
              sign: 'Face and lips turn blue',
              severity: 'Critical'
            },
            {
              order: 5,
              sign: 'Loss of consciousness',
              severity: 'Life-threatening'
            }
          ],
          additionalSigns: [
            {
              type: 'Hand Signals',
              description: 'They may place their hands around their throat or wave their arms.'
            },
            {
              type: 'Facial Expressions',
              description: 'Their faces may turn red, and they may appear panicked.'
            },
            {
              type: 'Skin Colour',
              description: 'If the airway remains blocked, their skin may turn blue due to lack of oxygen.'
            }
          ]
        },
        airwayObstructionTypes: {
          title: 'Types of Airway Obstruction',
          mildObstruction: {
            title: 'Signs of mild airway obstruction',
            signs: [
              'Response to question \'Are you choking?\': Victim speaks and answers yes',
              'Other signs: Victim is able to speak, cough, and breathe'
            ]
          },
          severeObstruction: {
            title: 'Signs of severe airway obstruction',
            signs: [
              'Response to question \'Are you choking?\': Victim unable to speak, may respond by nodding',
              'Victim unable to breathe',
              'Breathing sounds wheezy',
              'Attempts at coughing are silent',
              'Victim may be unconscious'
            ]
          }
        },
        keyPoints: [
          'Choking is a life-threatening emergency that requires immediate action',
          'Recognition of signs and symptoms is crucial for appropriate response',
          'The severity of airway obstruction determines the appropriate intervention',
          'Quick assessment and proper response can save lives'
        ]
      },
      quiz: {
        title: 'Understanding Choking Emergencies',
        description: 'Test your knowledge about choking causes, signs, and severity assessment',
        questions: [
          {
            id: 'choking-emergency-1',
            question: 'Which of the following is NOT a common cause of choking?',
            options: [
              'Eating too quickly',
              'Sleeping while eating',
              'Laughing while eating',
              'Wearing dentures'
            ],
            correctAnswer: 1,
            explanation: 'Sleeping while eating is not a common cause of choking as people generally don\'t eat while sleeping. The other options are all common risk factors for choking.'
          },
          {
            id: 'choking-emergency-2',
            question: 'What is a sign of severe airway obstruction?',
            options: [
              'Able to speak clearly',
              'Strong, forceful coughing',
              'Silent cough attempts',
              'Normal skin color'
            ],
            correctAnswer: 2,
            explanation: 'Silent cough attempts are a significant sign of severe airway obstruction. The ability to speak, forceful coughing, and normal skin color indicate mild obstruction or no obstruction.'
          },
          {
            id: 'choking-emergency-3',
            question: 'How can you initially assess if someone is choking?',
            options: [
              'Ask them to run in place',
              'Ask \'Are you choking?\'',
              'Wait for them to fall unconscious',
              'Immediately start chest compressions'
            ],
            correctAnswer: 1,
            explanation: 'Asking \'Are you choking?\' is the first step in assessing a potential choking victim. Their ability or inability to respond verbally helps determine the severity of the obstruction.'
          }
        ]
      }
    }
  },
  
  {
    id: '7.3',
    title: 'Choking Algorithm',
    description: 'Step-by-step choking response',
    duration: 20,
    type: 'Interactive Lesson',
    content: {
      introduction: 'Master the step-by-step algorithm for responding to choking emergencies. This lesson covers the assessment of choking severity and the appropriate interventions for both conscious and unconscious victims.',
      objectives: [
        'Learn to assess the severity of airway obstruction',
        'Master the techniques for back blows and abdominal thrusts',
        'Understand when and how to transition between different interventions',
        'Know when to activate emergency medical services'
      ],
      mainContent: {
        algorithm: {
          title: 'Choking Management Algorithm',
          description: 'A systematic approach to managing choking emergencies based on severity and consciousness level.',
          lessonImages: [
            {
              src: '/images/chokingalgo/c1.png',
              alt: 'Initial Choking Assessment',
              caption: 'Assessing choking severity in a victim'
            },
            {
              src: '/images/chokingalgo/c2.png',
              alt: 'Back Blows Technique',
              caption: 'Proper technique for delivering back blows'
            },
            {
              src: '/images/chokingalgo/c3.png',
              alt: 'Abdominal Thrust Technique',
              caption: 'Proper positioning for abdominal thrusts (Heimlich maneuver)'
            },
            {
              src: '/images/chokingalgo/c4.png',
              alt: 'Choking Algorithm Flowchart',
              caption: 'Complete choking management algorithm flowchart'
            }
          ],
          steps: [
            {
              title: 'Initial Assessment',
              description: 'Determine the severity of airway obstruction',
              branches: [
                {
                  condition: 'Severe Airway Obstruction',
                  description: 'Ineffective cough',
                  outcomes: [
                    {
                      state: 'Unconscious',
                      action: 'Start CPR'
                    },
                    {
                      state: 'Conscious',
                      action: '5 back blows followed by 5 abdominal thrusts'
                    }
                  ]
                },
                {
                  condition: 'Mild Airway Obstruction',
                  description: 'Effective cough',
                  action: 'Encourage to continue coughing and monitor closely',
                  outcomes: [
                    {
                      state: 'Unconscious',
                      action: 'Start CPR'
                    },
                    {
                      state: 'Conscious',
                      action: 'Do not interfere — monitor and reassess continuously'
                    }
                  ]
                }
              ]
            }
          ]
        },
        procedures: {
          backBlows: {
            title: 'Performing Back Blows',
            steps: [
              'Ensure any obvious obstruction in the mouth is removed',
              'Inform the patient of what you are about to do',
              'Stand beside the patient, lean them forward whilst supporting their chest with one hand',
              'Deliver up to 5 sharp blows between the shoulder blades, using the heel of one hand'
            ]
          },
          abdominalThrusts: {
            title: 'Performing Abdominal Thrusts',
            steps: [
              'Inform the patient of what you are about to do',
              'Stand behind the patient, whether he/she is sitting or standing',
              'Put both arms around the patient\'s abdomen, leaning the patient forward',
              'Clench one hand into a fist and place it between the umbilicus (belly button) and the xiphisternum (bottom tip of the breastbone) and grasp the fist with your other hand',
              'Pull sharply, inwards and upwards',
              'Deliver up to 5 abdominal thrusts'
            ]
          }
        },
        responseGuidelines: {
          title: 'Response Guidelines',
          sections: [
            {
              title: 'Initial Response',
              steps: [
                'Quickly assess if the person can speak, cough, or breathe',
                'Ask "Are you choking?" to confirm the situation',
                'Look for universal choking sign (hands clutched to throat)'
              ]
            },
            {
              title: 'Immediate Actions',
              steps: [
                'For conscious victim: Stand behind and slightly to one side',
                'Support chest with one hand and lean victim forward',
                'Give up to 5 sharp back blows between shoulder blades',
                'Check if object is expelled after each blow'
              ]
            },
            {
              title: 'Follow-up Care',
              steps: [
                'Monitor breathing and consciousness',
                'Encourage normal breathing if obstruction is cleared',
                'Recommend medical evaluation after severe choking incident',
                'Document the incident and response provided'
              ]
            }
          ],
          specialConsiderations: [
            'For pregnant women or obese persons: Give chest thrusts instead of abdominal thrusts',
            'For infants: Use modified back blows and chest thrusts with less force',
            'Call emergency services immediately for unconscious victims'
          ]
        }
      },
      procedures: {
        backBlows: {
          title: 'Performing Back Blows',
          steps: [
            'Ensure any obvious obstruction in the mouth is removed',
            'Inform the patient of what you are about to do',
            'Stand beside the patient, lean them forward whilst supporting their chest with one hand',
            'Deliver up to 5 sharp blows between the shoulder blades, using the heel of one hand'
          ]
        },
        abdominalThrusts: {
          title: 'Performing Abdominal Thrusts',
          steps: [
            'Inform the patient of what you are about to do',
            'Stand behind the patient, whether he/she is sitting or standing',
            'Put both arms around the patient\'s abdomen, leaning the patient forward',
            'Clench one hand into a fist and place it between the umbilicus (belly button) and the xiphisternum (bottom tip of the breastbone) and grasp the fist with your other hand',
            'Pull sharply, inwards and upwards',
            'Deliver up to 5 abdominal thrusts'
          ]
        }
      },
      emergencyProtocol: {
        title: 'Emergency Response Protocol',
        steps: [
          'If the patient shows signs of exhaustion (weak cough, cyanosis, breathing problems) but is still conscious:',
          'Carry out up to 5 back blows',
          'If obstruction persists, deliver up to 5 abdominal thrusts',
          'Continue alternating between back blows and abdominal thrusts until obstruction is expelled or patient loses consciousness',
          'If patient loses consciousness, start a BLS assessment',
          'Call the Cardiac Arrest Team if two cycles of blows and thrusts do not relieve the obstruction'
        ]
      },
      keyPoints: [
        'Quick assessment and appropriate response are crucial',
        'The severity of obstruction determines the intervention',
        'Alternate between back blows and abdominal thrusts for severe obstruction',
        'Be prepared to start CPR if the patient becomes unconscious',
        'Call for emergency help if initial interventions are not effective'
      ],
      quiz: {
        title: 'Choking Algorithm Assessment',
        description: 'Test your understanding of the choking management algorithm and intervention techniques',
        questions: [
          {
            id: 'choking-algorithm-1',
            question: 'What is the first step in the choking management algorithm?',
            options: [
              'Start back blows immediately',
              'Assess the severity of obstruction',
              'Perform abdominal thrusts',
              'Call emergency services'
            ],
            correctAnswer: 1,
            explanation: 'The first step is always to assess the severity of the obstruction to determine the appropriate intervention.'
          },
          {
            id: 'choking-algorithm-2',
            question: 'For a conscious patient with severe airway obstruction, what is the correct sequence of interventions?',
            options: [
              'Start CPR immediately',
              'Only perform abdominal thrusts',
              '5 back blows followed by 5 abdominal thrusts',
              'Only perform back blows'
            ],
            correctAnswer: 2,
            explanation: 'For a conscious patient with severe obstruction, the correct sequence is 5 back blows followed by 5 abdominal thrusts.'
          },
          {
            id: 'choking-algorithm-3',
            question: 'What should you do if a choking patient becomes unconscious?',
            options: [
              'Continue abdominal thrusts',
              'Wait for emergency services',
              'Start CPR',
              'Perform more back blows'
            ],
            correctAnswer: 2,
            explanation: 'If a choking patient becomes unconscious, you should immediately start CPR as per BLS protocols.'
          }
        ]
      }
    }
  },
  {
    id: '7.4',
    title: 'Anaphylaxis',
    description: 'Managing anaphylactic reactions',
    duration: 25,
    type: 'lesson',
    content: {
      introduction: 'Anaphylaxis is a severe, potentially life-threatening allergic reaction that occurs rapidly and requires immediate medical attention. It is characterised by a sudden onset of symptoms affecting multiple body systems, including the skin, respiratory, cardiovascular, and gastrointestinal systems. Prompt recognition and treatment are crucial to prevent serious complications or death.',
      objectives: [
        'Understand what anaphylaxis is and its causes',
        'Recognize common triggers and risk factors',
        'Identify signs and symptoms across different body systems',
        'Learn proper adrenaline administration techniques',
        'Understand emergency response protocols'
      ],
      mainContent: {
        triggers: {
          title: 'Common Triggers',
          description: 'Anaphylaxis can be triggered by a variety of allergens. Common causes include:',
          categories: [
            {
              title: 'Foods',
              items: ['Peanuts', 'Tree nuts', 'Shellfish', 'Fish', 'Milk', 'Eggs', 'Soy'],
              icon: 'restaurant'
            },
            {
              title: 'Medications',
              description: 'Antibiotics (e.g., penicillin), non-steroidal anti-inflammatory drugs (NSAIDs), and certain vaccines.',
              icon: 'medication'
            },
            {
              title: 'Insect Stings',
              items: ['Bees', 'Wasps', 'Hornets', 'Ants'],
              icon: 'pest_control'
            },
            {
              title: 'Latex',
              description: 'Products made from natural rubber latex, such as gloves or balloons.',
              icon: 'health_and_safety'
            },
            {
              title: 'Others',
              description: 'Exercise-induced anaphylaxis, idiopathic anaphylaxis (unknown causes).',
              icon: 'more_horiz'
            }
          ]
        },
        symptoms: {
          title: 'Signs and Symptoms',
          description: 'The symptoms of anaphylaxis can vary but typically include:',
          categories: [
            {
              system: 'Skin',
              symptoms: [
                'Hives',
                'Itching',
                'Flushed or pale skin',
                'Swelling of the face, lips, eyes, or throat (angioedema)'
              ],
              severity: 'warning'
            },
            {
              system: 'Respiratory',
              symptoms: [
                'Difficulty breathing',
                'Wheezing',
                'Nasal congestion',
                'Coughing',
                'Throat tightness',
                'Hoarseness',
                'Stridor'
              ],
              severity: 'error'
            },
            {
              system: 'Cardiovascular',
              symptoms: [
                'Rapid, weak pulse',
                'Dizziness',
                'Fainting',
                'Low blood pressure'
              ],
              severity: 'error'
            },
            {
              system: 'Gastrointestinal',
              symptoms: [
                'Nausea',
                'Vomiting',
                'Diarrhea',
                'Abdominal cramps'
              ],
              severity: 'warning'
            }
          ]
        },
        treatment: {
          title: 'Adrenaline Administration',
          sections: [
            {
              title: 'Administration',
              content: 'Administer adrenaline intramuscularly (IM) into the mid-outer thigh as soon as anaphylaxis is suspected. Use an adrenaline auto-injector if available (e.g., EpiPen, Jext).'
            },
            {
              title: 'Dosage',
              content: 'The standard dose for adults and children over 12 years is 0.5 mg (0.5 mL of 1:1000 solution). For children, the dose is typically 0.01 mg/kg of body weight, up to 0.3 mg.'
            },
            {
              title: 'Repeat Dose',
              content: 'If there is no improvement in symptoms after 5-10 minutes, a second dose may be administered.'
            }
          ]
        }
      },
      keyPoints: [
        'Anaphylaxis is a medical emergency requiring immediate treatment',
        'Early recognition of symptoms is crucial for survival',
        'Adrenaline is the first-line treatment and should be administered promptly',
        'Always call emergency services after administering adrenaline',
        'Monitor patient closely for biphasic reactions'
      ],
      quiz: {
        title: 'Anaphylaxis Management Assessment',
        description: 'Test your understanding of anaphylaxis recognition and treatment',
        questions: [
          {
            id: 'anaphylaxis-1',
            question: 'Which of the following is NOT a common trigger for anaphylaxis?',
            options: [
              'Peanuts and tree nuts',
              'Common cold virus',
              'Bee stings',
              'Latex gloves'
            ],
            correctAnswer: 1,
            explanation: 'The common cold virus does not trigger anaphylaxis. Common triggers include foods (nuts, shellfish), medications, insect stings, and latex.'
          },
          {
            id: 'anaphylaxis-2',
            question: 'What is the first-line treatment for anaphylaxis?',
            options: [
              'Oral antihistamines',
              'Intravenous fluids',
              'Adrenaline (epinephrine)',
              'Oxygen therapy'
            ],
            correctAnswer: 2,
            explanation: 'Adrenaline (epinephrine) is the first-line treatment for anaphylaxis and should be administered immediately when anaphylaxis is suspected.'
          },
          {
            id: 'anaphylaxis-3',
            question: 'Where should adrenaline be administered in cases of anaphylaxis?',
            options: [
              'In the arm muscle',
              'In the mid-outer thigh',
              'In the buttocks',
              'Subcutaneously in the abdomen'
            ],
            correctAnswer: 1,
            explanation: 'Adrenaline should be administered intramuscularly into the mid-outer thigh for optimal absorption and effectiveness.'
          }
        ]
      }
    }
  },
];

export const module8_lessons = [
  {
    id: '8.1',
    title: 'Conclusion Summary',
    description: 'Course review and final summary',
    duration: 30,
    type: 'Summary',
    content: {
      introduction: 'Congratulations on completing the BLS CPR course! This comprehensive summary will help reinforce the critical knowledge and skills you have acquired throughout your training. Remember that regular practice and refresher courses are essential to maintain your life-saving capabilities.',
      objectives: [
        'Review and consolidate key concepts from all modules',
        'Reinforce critical emergency response protocols',
        'Understand the importance of continuous practice and updates',
        'Prepare for certification assessment'
      ],
      mainContent: {
        courseOverview: {
          title: 'Course Overview',
          description: 'Throughout this course, you have covered essential aspects of Basic Life Support and CPR:',
          modules: [
            {
              title: 'Introduction to BLS',
              keyPoints: [
                'Understanding the importance and purpose of BLS',
                'Legal and ethical considerations',
                'Chain of survival concept'
              ],
              icon: 'menu_book'
            },
            {
              title: 'Patient Assessment',
              keyPoints: [
                'ABCDE approach to patient assessment',
                'Recognition of cardiac arrest',
                'Early warning signs and symptoms'
              ],
              icon: 'medical_information'
            },
            {
              title: 'CPR Techniques',
              keyPoints: [
                'High-quality chest compressions',
                'Effective rescue breaths',
                'Compression-to-ventilation ratios'
              ],
              icon: 'health_and_safety'
            },
            {
              title: 'AED Usage',
              keyPoints: [
                'AED operation and maintenance',
                'Pad placement techniques',
                'Safety considerations'
              ],
              icon: 'monitor_heart'
            },
            {
              title: 'Special Circumstances',
              keyPoints: [
                'Choking management',
                'Anaphylaxis response',
                'Post-resuscitation care'
              ],
              icon: 'warning'
            }
          ]
        },
        criticalSkills: {
          title: 'Critical Skills Checklist',
          description: 'Ensure proficiency in these essential skills:',
          skills: [
            {
              category: 'Assessment',
              items: [
                'Scene safety evaluation',
                'Consciousness check',
                'Breathing assessment',
                'Pulse check'
              ],
              icon: 'visibility'
            },
            {
              category: 'CPR Performance',
              items: [
                'Proper hand position',
                'Compression depth and rate',
                'Complete chest recoil',
                'Minimal interruptions'
              ],
              icon: 'fitness_center'
            },
            {
              category: 'AED Operation',
              items: [
                'Pad placement',
                'Clear victim check',
                'Shock delivery',
                'CPR resumption'
              ],
              icon: 'bolt'
            },
            {
              category: 'Emergency Response',
              items: [
                'Activation of emergency services',
                'Team communication',
                'Role coordination',
                'Handover procedures'
              ],
              icon: 'emergency'
            }
          ]
        },
        nextSteps: {
          title: 'Next Steps',
          sections: [
            {
              title: 'Certification',
              content: 'Complete your certification assessment within 30 days of course completion. The assessment will evaluate both theoretical knowledge and practical skills.',
              icon: 'card_membership'
            },
            {
              title: 'Regular Practice',
              content: 'Schedule regular practice sessions to maintain your skills. Consider monthly refresher drills with your team.',
              icon: 'update'
            },
            {
              title: 'Stay Updated',
              content: 'Keep informed about the latest BLS guidelines and protocols. Guidelines are typically updated every 5 years.',
              icon: 'new_releases'
            }
          ]
        }
      },
      keyPoints: [
        'BLS skills require regular practice to maintain proficiency',
        'Always prioritize scene safety and personal protection',
        'Early recognition and response are crucial for survival',
        'High-quality CPR is essential for positive outcomes',
        'Effective teamwork and communication save lives'
      ],
      quiz: {
        title: 'Final Knowledge Check',
        description: 'Test your understanding of key BLS concepts',
        questions: [
          {
            id: 'final-check-1',
            question: 'What is the correct sequence in the Chain of Survival?',
            options: [
              'Early CPR, Early Recognition, Early Defibrillation, Post-resuscitation Care',
              'Early Recognition, Early CPR, Early Defibrillation, Post-resuscitation Care',
              'Early Defibrillation, Early Recognition, Early CPR, Post-resuscitation Care',
              'Early Recognition, Early Defibrillation, Early CPR, Post-resuscitation Care'
            ],
            correctAnswer: 1,
            explanation: 'The Chain of Survival sequence is: Early Recognition and Response, Early CPR, Early Defibrillation, and Post-resuscitation Care.'
          },
          {
            id: 'final-check-2',
            question: 'What is the recommended depth for adult chest compressions?',
            options: [
              '1-2 inches (3-5 cm)',
              '2-2.4 inches (5-6 cm)',
              '3-4 inches (7.5-10 cm)',
              '0.5-1 inch (1.5-2.5 cm)'
            ],
            correctAnswer: 1,
            explanation: 'For adults, compress the chest at least 2-2.4 inches (5-6 cm) for optimal blood flow.'
          },
          {
            id: 'final-check-3',
            question: 'Which of the following is NOT a sign of high-quality CPR?',
            options: [
              'Complete chest recoil after each compression',
              'Minimal interruptions in compressions',
              'Compression rate of 60-80 per minute',
              'Proper hand placement on lower half of sternum'
            ],
            correctAnswer: 2,
            explanation: 'The correct compression rate is 100-120 compressions per minute. 60-80 is too slow for effective circulation.'
          }
        ]
      }
    }
  }
];

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

function BLSCPRCourse() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('learning');
  const [expandedModule, setExpandedModule] = useState(() => {
    const saved = localStorage.getItem('blsCprExpandedModule');
    return saved !== null ? parseInt(saved) : 0;
  });
  const [completedLessons, setCompletedLessons] = useState(getCompletedLessons());
  const [currentLesson, setCurrentLesson] = useState(null);
  const { moduleProgress, setModuleProgress } = useModuleProgress();
  const [isModuleStarted, setIsModuleStarted] = useState(false);

  const handleModuleChange = (moduleIndex) => {
    const newExpandedModule = moduleIndex === expandedModule ? -1 : moduleIndex;
    setExpandedModule(newExpandedModule);
    localStorage.setItem('blsCprExpandedModule', newExpandedModule.toString());
  };

  const handleStartLesson = (moduleId, lesson) => {
    setCurrentLesson(lesson);
    navigate(`/bls-cpr/${moduleId}/${lesson.id}`);
    scrollToTop();
  };

  const calculateTotalDuration = (lessons) => {
    return lessons.reduce((total, lesson) => total + (lesson.duration || 0), 0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          onClick={() => navigate('/courses')} 
          variant="outlined" 
          startIcon={<SchoolIcon />}
          sx={{ mb: 2 }}
        >
          Back to Courses
        </Button>
        <Chip
          icon={<AccessibilityIcon />}
          label={isModuleStarted ? 'In Progress' : 'Not Started'}
          color={isModuleStarted ? 'primary' : 'default'}
          variant="outlined"
        />
      </Box>
      
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Basic Life Support (BLS) and CPR Training
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Tabs 
          value={activeSection} 
          onChange={(e, newValue) => setActiveSection(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Course Content" value="learning" />
        </Tabs>
      </Box>

      <TabPanel value={activeSection} index="learning">
        {/* Module 1: Introduction */}
        {/* <Box>
          <CourseModule
            module={{
              id: "intro",
              title: "Introduction to BLS and CPR",
              description: "Overview of Basic Life Support and CPR fundamentals",
              icon: <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module1_lessons),
              lessons: module1_lessons
            }}
            expanded={expandedModule === 0}
            onChange={() => handleModuleChange(0)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('intro', lesson)}
            courseName="blscpr"
          />
        </Box> */}

        {/* Module 2: Patient Assessment */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "assessment",
              title: "Patient Assessment and Scene Safety",
              description: "Learn to assess patients and ensure scene safety",
              icon: <HealthAndSafetyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module2_lessons),
              lessons: module2_lessons
            }}
            expanded={expandedModule === 1}
            onChange={() => handleModuleChange(1)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('assessment', lesson)}
            courseName="blscpr"
          />
        </Box>

        {/* Module 3: Adult BLS */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "adult-bls",
              title: "Recognizing an Acutely Unwell Patient",
              description: "Adult CPR techniques and protocols",
              icon: <AccessibilityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module3_lessons),
              lessons: module3_lessons
            }}
            expanded={expandedModule === 2}
            onChange={() => handleModuleChange(2)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('adult-bls', lesson)}
            courseName="blscpr"
          />
        </Box>

        {/* Module 4: Adult Basic Life Support */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "cpr-techniques",
              title: "Adult Basic Life Support",
              description: "Master essential adult BLS techniques and protocols",
              icon: <LocalHospitalIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module4_lessons),
              lessons: module4_lessons
            }}
            expanded={expandedModule === 3}
            onChange={() => handleModuleChange(3)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('cpr-techniques', lesson)}
            courseName="blscpr"
          />
        </Box>

        {/* Module 5: AED */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "aed",
              title: "Automated External Defibrillators (AEDs)",
              description: "Learn proper AED usage and safety protocols",
              icon: <EmergencyShareIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module5_lessons),
              lessons: module5_lessons
            }}
            expanded={expandedModule === 4}
            onChange={() => handleModuleChange(4)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('aed', lesson)}
            courseName="blscpr"
          />
        </Box>

        {/* Module 6: Post-Resuscitation Care */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "post-resuscitation",
              title: "Post-Resuscitation Care and Documentation",
              description: "Learn proper post-resuscitation care and documentation procedures",
              icon: <HealthAndSafetyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module6_lessons),
              lessons: module6_lessons
            }}
            expanded={expandedModule === 5}
            onChange={() => handleModuleChange(5)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('post-resuscitation', lesson)}
            courseName="blscpr"
          />
        </Box>

        {/* Module 7: Emergency Situations */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "emergency-response",
              title: "Responding to Emergency Situations",
              description: "Handle choking, anaphylaxis, and other emergencies",
              icon: <EmergencyShareIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module7_lessons),
              lessons: module7_lessons
            }}
            expanded={expandedModule === 6}
            onChange={() => handleModuleChange(6)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('emergency-response', lesson)}
            courseName="blscpr"
          />
        </Box>

        {/* Module 8: Conclusion */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "conclusion",
              title: "Conclusion Summary",
              description: "Course review and final assessment",
              icon: <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module8_lessons),
              lessons: module8_lessons
            }}
            expanded={expandedModule === 7}
            onChange={() => handleModuleChange(7)}
            completedLessons={completedLessons}
            currentLesson={currentLesson}
            onStartLesson={(lesson) => handleStartLesson('conclusion', lesson)}
            courseName="blscpr"
          />
        </Box>
      </TabPanel>


    </Container>
  );
}

export default BLSCPRCourse;
